<template>
  <div class="kg-md-editor-page">
    <!-- Plain page header (chatframework list-page style — mirrors FlowList/KnowledgeGraphList) -->
    <v-row align="center" class="mx-2 mt-4 mb-1">
      <v-col cols="12" md="8" class="d-flex align-center">
        <v-btn id="button-back-kgMdEditor" icon variant="text" class="mr-1" @click="onBack">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <h2 style="font-weight: 600; color: #171717;">{{ $t("kgme_title") }}</h2>
        </div>
      </v-col>
      <v-col cols="12" md="4" class="d-flex justify-end flex-wrap" style="gap: 8px;">
        <!-- S16-P1 P4: data-testid (kgme-doc-chip · kgme-mode-chip) -->
        <v-chip v-if="vectorDBName" data-testid="kgme-doc-chip" variant="outlined" color="#C32E0E" class="font-weight-medium" label>
          <v-icon start size="small">mdi-file-document-outline</v-icon>{{ vectorDBName }}
        </v-chip>
        <v-chip v-if="!['chunks', 'mode'].includes(step)" data-testid="kgme-mode-chip" variant="outlined" size="small">
          {{ chunkMode === "Combined" ? $t("kgme_subtitleCombined") : $t("kgme_subtitleBySection") }}
        </v-chip>
      </v-col>
    </v-row>

    <v-card class="card2 mt-2 mx-2">
      <!-- Loading overlay -->
      <v-overlay :model-value="loading" contained class="align-center justify-center">
        <!-- S16-P1 P4: data-testid -->
        <div class="text-center" data-testid="kgme-loading">
          <v-progress-circular indeterminate size="48" color="#C32E0E" />
          <div class="mt-3 text-body-2">{{ $t("kgme_preparing") }}</div>
        </div>
      </v-overlay>

      <!-- Horizontal stepper — shown across the whole flow from the first step. The step list is
           dynamic: the Configure/Build steps only appear once the user opts into the AI path. -->
      <div class="kg-stepper-wrap">
        <v-stepper :model-value="currentStepNumber" flat class="kg-stepper" hide-actions @update:model-value="onStepClick">
          <v-stepper-header>
            <template v-for="(s, i) in wizardSteps" :key="s.key">
              <!-- S16-P1 P4: data-testid -->
              <v-stepper-item
                :data-testid="`kgme-step-${s.key}`"
                :value="i + 1"
                :complete="currentStepNumber > i + 1"
                :title="s.title"
                color="#C32E0E"
                :editable="!building && visitedSteps.includes(s.key)"
                :class="{ 'kg-step-clickable': !building && visitedSteps.includes(s.key) && s.key !== step }"
              />
              <v-divider v-if="i < wizardSteps.length - 1" />
            </template>
          </v-stepper-header>
        </v-stepper>
      </div>

      <v-card-text class="kg-step-body">
        <!-- Step: select chunks -->
        <div v-if="step === 'chunks'">
          <div class="text-subtitle-1 font-weight-medium mb-1">
            <v-icon start color="#C32E0E">mdi-format-list-checks</v-icon>{{ $t("kgme_chunksTitle") }}
          </div>
          <div class="text-caption text-medium-emphasis mb-3">{{ $t("kgme_chunksHint") }}</div>

          <!-- S16-P1 T3: name the graph before selecting chunks -->
          <v-text-field
            id="input-graphname-kgMdEditor"
            v-model="graphName"
            :label="$t('kgme_graphName')"
            :hint="$t('kgme_graphNameHint')"
            persistent-hint
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-rename-box"
            class="mb-4"
            :error="graphNameTaken"
            :error-messages="graphNameTaken ? $t('kgme_graphNameTaken') : []"
          />

          <!-- S16-P1 P4: data-testid -->
          <div v-if="loadingChunks" class="py-8 text-center" data-testid="kgme-chunks-loading">
            <v-progress-circular indeterminate color="#C32E0E" />
          </div>
          <template v-else>
            <div class="d-flex align-center mb-2">
              <v-checkbox
                id="checkbox-selectall-kgMdEditor"
                :model-value="allChunksSelected"
                :indeterminate="someChunksSelected"
                color="#C32E0E"
                density="compact"
                hide-details
                @update:model-value="toggleSelectAll"
              >
                <template #label><span class="font-weight-medium">{{ $t("kgme_selectAll") }}</span></template>
              </v-checkbox>
              <v-spacer />
              <!-- S16-P1 P4: data-testid -->
              <span class="text-caption text-medium-emphasis" data-testid="kgme-chunk-counter">
                {{ $t("kgme_chunksSelectedPrefix") }} {{ selectedChunkIndexes.length }}/{{ allChunks.length }}
              </span>
            </div>
            <v-divider class="mb-2" />
            <div class="kg-chunk-list">
              <!-- S16-P1 P4: data-testid -->
              <v-card
                v-for="c in allChunks"
                :key="c.index"
                :data-testid="`kgme-chunk-card-${c.index}`"
                variant="outlined"
                class="kg-choice-card mb-2"
                :class="{ 'kg-choice-card--active': selectedChunkIndexes.includes(c.index) }"
                @click="toggleChunk(c.index)"
              >
                <div class="d-flex align-start pa-3">
                  <!-- S16-P1 P4: data-testid -->
                  <v-checkbox
                    :data-testid="`kgme-chunk-checkbox-${c.index}`"
                    :model-value="selectedChunkIndexes.includes(c.index)"
                    color="#C32E0E"
                    density="compact"
                    hide-details
                    style="flex: 0 0 auto;"
                    @click.stop
                    @update:model-value="toggleChunk(c.index)"
                  />
                  <div class="ml-1" style="flex: 1 1 auto; text-align: left; min-width: 0;">
                    <div class="font-weight-medium">
                      {{ $t("kgme_chunkLabel") }} {{ c.index + 1 }}
                      <span class="text-caption text-medium-emphasis ml-1">· {{ c.charCount }} {{ $t("kgme_chunkCharsUnit") }}</span>
                    </div>
                    <div class="text-caption text-medium-emphasis kg-chunk-preview">{{ c.preview }}</div>
                  </div>
                </div>
              </v-card>
            </div>
            <div class="d-flex mt-3">
              <v-spacer />
              <v-btn
                id="button-chunks-next-kgMdEditor"
                color="#C32E0E"
                variant="elevated"
                append-icon="mdi-arrow-right"
                :disabled="selectedChunkIndexes.length === 0 || !graphName.trim() || graphNameTaken"
                @click="goFromChunks"
              >
                {{ $t("kgme_next") }}
              </v-btn>
            </div>
          </template>
        </div>

        <!-- Step: choose chunk mode -->
        <div v-else-if="step === 'mode'">
          <div class="text-subtitle-1 font-weight-medium mb-3">{{ $t("kgme_modeTitle") }}</div>
          <v-radio-group id="radio-chunkmode-kgMdEditor" v-model="chunkModeChoice" hide-details>
            <!-- S16-P1 P4: data-testid -->
            <v-card
              v-for="opt in modeOptions"
              :key="opt.value"
              :data-testid="`kgme-mode-${opt.value}`"
              variant="outlined"
              class="kg-choice-card mb-3"
              :class="{ 'kg-choice-card--active': chunkModeChoice === opt.value }"
              @click="chunkModeChoice = opt.value"
            >
              <div class="d-flex align-center pa-3">
                <v-radio :value="opt.value" density="compact" hide-details style="flex: 0 0 auto;" />
                <div class="ml-1" style="flex: 1 1 auto; text-align: left; min-width: 0;">
                  <div class="font-weight-medium">{{ $t(opt.title) }}</div>
                  <div class="text-caption text-medium-emphasis">{{ $t(opt.desc) }}</div>
                </div>
              </div>
            </v-card>
          </v-radio-group>
          <div class="d-flex mt-2">
            <v-btn id="button-mode-back-kgMdEditor" variant="text" prepend-icon="mdi-arrow-left" @click="onBack">{{ $t("kgme_back") }}</v-btn>
            <v-spacer />
            <v-btn id="button-chunkmode-next-kgMdEditor" color="#C32E0E" variant="elevated" append-icon="mdi-arrow-right" @click="chooseMode(chunkModeChoice)">
              {{ $t("kgme_next") }}
            </v-btn>
          </div>
        </div>

        <!-- Step: Configure (AI path) — left: raw document data (no AI, editable); right: model/prompt + build.
             The build (progress / per-chunk error / regenerate) all happens here; there is no separate step. -->
        <div v-else-if="step === 'llm'">
          <v-row>
            <!-- Left column: document data (no AI) — editable chunk sources -->
            <v-col cols="12" md="6" class="kg-config-col">
              <div class="text-subtitle-1 font-weight-medium mb-1">
                <v-icon start color="#C32E0E">mdi-file-document-edit-outline</v-icon>{{ $t("kgme_leftDocTitle") }}
              </div>
              <div class="text-caption text-medium-emphasis mb-3">{{ $t("kgme_leftDocHint") }}</div>
              <!-- Combined mode: the document as one or more editable SECTIONS. One section = the whole
                   joined document (single LLM call). Splitting/adding produces more sections, each
                   generated as its OWN call — the way to fit a too-large document under the context
                   window while still assembling one final document. -->
              <div v-if="chunkMode === 'Combined'" class="kg-chunk-list">
                <!-- S16-P1 P4: data-testid kgme-left-*-{n} (n = section index i) -->
                <v-card
                  v-for="(sec, i) in combinedSections"
                  :key="'sec-' + i"
                  :data-testid="`kgme-left-card-${i}`"
                  variant="outlined"
                  class="mb-2"
                  :class="{ 'kg-batch-error': chunkStatus(i) === 'error' }"
                >
                  <div class="pa-3">
                    <div class="d-flex align-center mb-1">
                      <!-- S16-P1 P4: data-testid -->
                      <v-icon
                        v-if="chunkStatus(i)"
                        :data-testid="`kgme-left-status-${i}`"
                        size="18"
                        class="mr-1"
                        :color="statusColor(chunkStatus(i))"
                        :class="{ 'mdi-spin': chunkStatus(i) === 'running' }"
                        style="flex: 0 0 auto;"
                      >{{ statusIcon(chunkStatus(i)) }}</v-icon>
                      <span class="font-weight-medium">
                        {{ combinedSections.length > 1 ? ($t("kgme_sectionLabel") + " " + (i + 1)) : $t("kgme_subtitleCombined") }}
                      </span>
                      <span class="text-caption text-medium-emphasis ml-1">· {{ (sec || '').length }} {{ $t("kgme_chunkCharsUnit") }}</span>
                      <v-spacer />
                      <!-- split this section into two (each its own LLM call) -->
                      <v-tooltip :text="$t('kgme_splitChunk')" location="top">
                        <template #activator="{ props }">
                          <!-- S16-P1 P4: data-testid -->
                          <v-btn
                            v-bind="props"
                            :data-testid="`kgme-left-split-${i}`"
                            icon="mdi-call-split"
                            size="x-small"
                            :variant="chunkStatus(i) === 'error' ? 'tonal' : 'text'"
                            :color="chunkStatus(i) === 'error' ? '#d33' : '#C32E0E'"
                            :disabled="building"
                            @click="splitSection(i)"
                          />
                        </template>
                      </v-tooltip>
                      <!-- add a new empty section after this one -->
                      <v-tooltip :text="$t('kgme_newChunk')" location="top">
                        <template #activator="{ props }">
                          <!-- S16-P1 P4: data-testid -->
                          <v-btn
                            v-bind="props"
                            :data-testid="`kgme-left-add-${i}`"
                            icon="mdi-note-plus-outline"
                            size="x-small"
                            variant="text"
                            color="#C32E0E"
                            :disabled="building"
                            @click="addSection(i)"
                          />
                        </template>
                      </v-tooltip>
                      <!-- delete this section (disabled when it's the only one) -->
                      <v-tooltip :text="$t('kgme_deleteChunk')" location="top">
                        <template #activator="{ props }">
                          <!-- S16-P1 P4: data-testid -->
                          <v-btn
                            v-bind="props"
                            :data-testid="`kgme-left-delete-${i}`"
                            icon="mdi-delete"
                            size="x-small"
                            variant="text"
                            color="#d33"
                            :disabled="building || combinedSections.length <= 1"
                            @click="deleteSection(i)"
                          />
                        </template>
                      </v-tooltip>
                      <v-tooltip :text="$t('kgme_expand')" location="top">
                        <template #activator="{ props }">
                          <!-- S16-P1 P4: data-testid -->
                          <v-btn
                            v-bind="props"
                            :data-testid="`kgme-left-expand-${i}`"
                            icon="mdi-arrow-expand"
                            size="x-small"
                            variant="text"
                            :disabled="building"
                            @click="openExpand($t('kgme_subtitleCombined'), combinedSections[i], (v) => { combinedSections[i] = v; onCombinedSectionEdited(i); })"
                          />
                        </template>
                      </v-tooltip>
                    </div>
                    <!-- S16-P1 P4: data-testid -->
                    <v-alert
                      v-if="chunkStatus(i) === 'error'"
                      :data-testid="`kgme-left-error-${i}`"
                      type="error"
                      variant="tonal"
                      density="compact"
                      rounded="lg"
                      class="mb-2 kg-chunk-error-alert"
                    >
                      {{ chunkError(i) }}
                    </v-alert>
                    <!-- S16-P1 P4: data-testid -->
                    <v-textarea
                      v-model="combinedSections[i]"
                      :data-testid="`kgme-left-textarea-${i}`"
                      variant="outlined"
                      rows="12"
                      no-resize
                      hide-details
                      density="compact"
                      class="kg-md-textarea"
                      :disabled="building"
                      @input="onCombinedSectionEdited(i)"
                    />
                  </div>
                </v-card>
              </div>
              <!-- BySection mode: per-chunk editable sources -->
              <div v-else class="kg-chunk-list">
                <!-- S16-P1 P4: data-testid kgme-left-*-{n} (n = c.index) -->
                <v-card
                  v-for="c in selectedChunkObjects"
                  :key="c.index"
                  :data-testid="`kgme-left-card-${c.index}`"
                  variant="outlined"
                  class="mb-2"
                  :class="{ 'kg-batch-error': chunkStatus(c.index) === 'error' }"
                >
                  <div class="pa-3">
                    <div class="d-flex align-center mb-1">
                      <!-- S16-P1 P4: data-testid -->
                      <v-icon
                        v-if="chunkStatus(c.index)"
                        :data-testid="`kgme-left-status-${c.index}`"
                        size="18"
                        class="mr-1"
                        :color="statusColor(chunkStatus(c.index))"
                        :class="{ 'mdi-spin': chunkStatus(c.index) === 'running' }"
                        style="flex: 0 0 auto;"
                      >{{ statusIcon(chunkStatus(c.index)) }}</v-icon>
                      <span class="font-weight-medium">{{ $t("kgme_chunkLabel") }} {{ c.index + 1 }}</span>
                      <span class="text-caption text-medium-emphasis ml-1">· {{ (c.content || '').length }} {{ $t("kgme_chunkCharsUnit") }}</span>
                      <v-spacer />
                      <!-- S16-P1 T9-2: split a chunk into two. Available on every chunk so an over-large chunk can
                           be pre-split before wasting a generate call; highlighted (red tonal) when that
                           chunk's build errored (e.g. input exceeded the model's context window). -->
                      <v-tooltip :text="$t('kgme_splitChunk')" location="top">
                        <template #activator="{ props }">
                          <!-- S16-P1 P4: data-testid -->
                          <v-btn
                            v-bind="props"
                            :data-testid="`kgme-left-split-${c.index}`"
                            icon="mdi-call-split"
                            size="x-small"
                            :variant="chunkStatus(c.index) === 'error' ? 'tonal' : 'text'"
                            :color="chunkStatus(c.index) === 'error' ? '#d33' : '#C32E0E'"
                            :disabled="building"
                            @click="splitChunk(c.index)"
                          />
                        </template>
                      </v-tooltip>
                      <!-- S16-P1 T-new: insert a fresh empty chunk right after this one for the user to fill in. -->
                      <v-tooltip :text="$t('kgme_newChunk')" location="top">
                        <template #activator="{ props }">
                          <!-- S16-P1 P4: data-testid -->
                          <v-btn
                            v-bind="props"
                            :data-testid="`kgme-left-add-${c.index}`"
                            icon="mdi-note-plus-outline"
                            size="x-small"
                            variant="text"
                            color="#C32E0E"
                            :disabled="building"
                            @click="addChunk(c.index)"
                          />
                        </template>
                      </v-tooltip>
                      <!-- S16-P1 T-del: split-derived chunk → hard delete + re-index; original chunk → un-select. -->
                      <v-tooltip :text="c.fromSplit ? $t('kgme_deleteChunk') : $t('kgme_unselectChunk')" location="top">
                        <template #activator="{ props }">
                          <!-- S16-P1 P4: data-testid -->
                          <v-btn
                            v-bind="props"
                            :data-testid="`kgme-left-delete-${c.index}`"
                            icon="mdi-delete"
                            size="x-small"
                            variant="text"
                            color="#d33"
                            :disabled="building"
                            @click="deleteChunk(c.index)"
                          />
                        </template>
                      </v-tooltip>
                      <v-tooltip :text="$t('kgme_expand')" location="top">
                        <template #activator="{ props }">
                          <!-- S16-P1 P4: data-testid -->
                          <v-btn
                            v-bind="props"
                            :data-testid="`kgme-left-expand-${c.index}`"
                            icon="mdi-arrow-expand"
                            size="x-small"
                            variant="text"
                            :disabled="building"
                            @click="openExpand($t('kgme_expandTitleChunk') + ' ' + (c.index + 1), c.content, (v) => { c.content = v; onChunkEdited(c.index); })"
                          />
                        </template>
                      </v-tooltip>
                    </div>
                    <!-- S16-P1 P4: data-testid -->
                    <v-alert
                      v-if="chunkStatus(c.index) === 'error'"
                      :data-testid="`kgme-left-error-${c.index}`"
                      type="error"
                      variant="tonal"
                      density="compact"
                      rounded="lg"
                      class="mb-2 kg-chunk-error-alert"
                    >
                      {{ chunkError(c.index) }}
                    </v-alert>
                    <!-- S16-P1 P4: data-testid -->
                    <v-textarea
                      v-model="c.content"
                      :data-testid="`kgme-left-textarea-${c.index}`"
                      variant="outlined"
                      rows="12"
                      no-resize
                      hide-details
                      density="compact"
                      class="kg-md-textarea"
                      :disabled="building"
                      @input="onChunkEdited(c.index)"
                    />
                  </div>
                </v-card>
              </div>
            </v-col>

            <!-- Right column: configure model + prompt + build -->
            <v-col cols="12" md="6" class="kg-config-col">
              <div class="text-subtitle-1 font-weight-medium mb-3">
                <v-icon start color="#C32E0E">mdi-robot-outline</v-icon>{{ $t("kgme_llmTitle") }}
              </div>
              <v-select
                id="select-llm-model-kgMdEditor"
                v-model="selectedModel"
                :items="llmModels"
                item-title="modelDisplay"
                item-value="modelName"
                :loading="loadingModels"
                :label="$t('kgme_selectModel')"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-cube-outline"
                hide-details
                class="mb-4"
                :disabled="building"
              >
                <template #item="{ props, item }">
                  <v-list-item v-bind="props" :title="item.raw.modelDisplay">
                    <template #subtitle>
                      <span class="text-caption font-weight-light">
                        {{ [item.raw.platform, item.raw.provider].filter(Boolean).join(' / ') }}
                      </span>
                    </template>
                  </v-list-item>
                </template>
              </v-select>
              <div class="d-flex align-center mb-1">
                <span class="text-subtitle-2">{{ $t("kgme_prompt") }}</span>
                <v-spacer />
                <v-btn
                  id="button-generate-prompt-kgMdEditor"
                  size="small"
                  variant="tonal"
                  color="#C32E0E"
                  :disabled="!selectedModel || building"
                  @click="openGenPromptDialog"
                >
                  <v-icon start size="small">mdi-auto-fix</v-icon>{{ $t("kgme_generatePrompt") }}
                </v-btn>
                <v-btn id="button-reset-prompt-kgMdEditor" size="small" variant="text" class="ml-1" :disabled="building" @click="llmPrompt = defaultPrompt">
                  {{ $t("kgme_resetPrompt") }}
                </v-btn>
                <v-tooltip :text="$t('kgme_expand')" location="top">
                  <template #activator="{ props }">
                    <!-- S16-P1 P4: data-testid -->
                    <v-btn
                      v-bind="props"
                      data-testid="kgme-prompt-expand"
                      icon="mdi-arrow-expand"
                      size="x-small"
                      variant="text"
                      class="ml-1"
                      @click="openExpand($t('kgme_expandTitlePrompt'), llmPrompt, (v) => { llmPrompt = v; })"
                    />
                  </template>
                </v-tooltip>
              </div>
              <v-textarea
                id="textarea-llm-prompt-kgMdEditor"
                v-model="llmPrompt"
                variant="outlined"
                rows="6"
                no-resize
                hide-details
                class="kg-config-prompt"
                :hint="$t('kgme_promptHint')"
                persistent-hint
                :disabled="building"
              />

              <!-- Chunks-per-call (BySection only; Combined sends the whole document in one call) -->
              <div v-if="chunkMode !== 'Combined'" class="mt-3">
                <v-text-field
                  id="input-batchsize-kgMdEditor"
                  v-model.number="llmBatchSize"
                  type="number"
                  min="1"
                  max="10"
                  :label="$t('kgme_batchSize')"
                  :hint="$t('kgme_batchSizeHint')"
                  persistent-hint
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-layers-triple-outline"
                  :disabled="building"
                />
              </div>

              <!-- Max output tokens (S16-P1 T2) — default = selected model's MaxToken; raise it so long docs
                   don't get truncated -->
              <div class="mt-3">
                <v-text-field
                  id="input-maxtokens-kgMdEditor"
                  v-model.number="maxTokens"
                  type="number"
                  min="256"
                  step="256"
                  :label="$t('kgme_maxTokens')"
                  :hint="$t('kgme_maxTokensHint')"
                  persistent-hint
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-counter"
                  :disabled="building"
                />
              </div>

              <!-- Build progress (shown after Build is clicked) -->
              <!-- S16-P1 P4: data-testid (kgme-build-progress · kgme-build-pct) -->
              <div v-if="batches.length" class="mt-4" data-testid="kgme-build-progress">
                <div class="d-flex align-center mb-1">
                  <span class="text-caption font-weight-medium">{{ $t("kgme_buildProgressLabel") }}</span>
                  <v-spacer />
                  <span class="text-caption font-weight-bold" data-testid="kgme-build-pct" :style="{ color: chunkErrorCount ? '#d33' : '#C32E0E' }">{{ overallProgressPct }}%</span>
                </div>
                <v-progress-linear
                  :model-value="overallProgressPct"
                  :color="chunkErrorCount ? '#d33' : '#C32E0E'"
                  :striped="building"
                  height="10"
                  rounded
                />
                <!-- Current activity + settled-chunk counts -->
                <div class="d-flex align-center mt-1">
                  <v-icon
                    v-if="building"
                    size="14"
                    color="#C32E0E"
                    class="mr-1"
                    :class="buildStatusIconClass"
                  >{{ buildStatusIcon }}</v-icon>
                  <v-icon v-else-if="buildAllDone && !buildHasError" size="14" color="success" class="mr-1">mdi-check-circle</v-icon>
                  <!-- S16-P1 P4: data-testid (kgme-build-status · kgme-build-counts) -->
                  <span class="text-caption text-medium-emphasis" data-testid="kgme-build-status">{{ buildStatusText }}</span>
                  <v-spacer />
                  <!-- BySection = chunk-based; Combined shows the section count only when split into >1 -->
                  <span v-if="chunkMode !== 'Combined' || combinedSections.length > 1" class="text-caption text-medium-emphasis" data-testid="kgme-build-counts">
                    {{ chunkDoneCount }}/{{ chunkTotal }} {{ $t("kgme_buildDoneWord") }}
                    <template v-if="chunkErrorCount"> · <span style="color: #d33; font-weight: 600;">{{ chunkErrorCount }} {{ $t("kgme_buildErrorWord") }}</span></template>
                  </span>
                </div>

                <!-- Realtime live-generation preview: the tokens of the batch currently streaming. -->
                <div v-if="liveBatch" class="kg-live mt-3">
                  <div class="d-flex align-center mb-1">
                    <v-icon size="13" color="#C32E0E" class="mr-1 kg-pulse">mdi-creation</v-icon>
                    <span class="text-caption font-weight-medium">{{ $t("kgme_liveGenerating") }}</span>
                  </div>
                  <!-- S16-P1 P4: data-testid -->
                  <pre ref="liveBox" class="kg-live-box" data-testid="kgme-live-box">{{ liveBatch.live }}<span class="kg-live-caret">▋</span></pre>
                </div>

                <!-- S16-P1 P4: data-testid -->
                <v-alert v-if="buildHasError && !building" data-testid="kgme-build-errors-remain" type="warning" variant="tonal" density="compact" rounded="lg" class="mt-3">
                  {{ $t("kgme_buildErrorsRemain") }}
                </v-alert>
              </div>
            </v-col>
          </v-row>

          <div class="d-flex mt-4">
            <v-btn id="button-llm-back-kgMdEditor" variant="text" prepend-icon="mdi-arrow-left" :disabled="building" @click="onBack">{{ $t("kgme_back") }}</v-btn>
            <v-spacer />
            <!-- Primary button changes with build state:
                 building → Stop · not started → Build · incomplete (stopped/errors) → Resume/Regenerate · all done → Next -->
            <v-btn
              v-if="building"
              id="button-stop-build-kgMdEditor"
              color="#d33"
              variant="elevated"
              prepend-icon="mdi-stop"
              @click="stopBuild"
            >
              {{ $t("kgme_stopBuild") }}
            </v-btn>
            <v-btn
              v-else-if="!batches.length"
              id="button-llm-create-kgMdEditor"
              color="#C32E0E"
              variant="elevated"
              prepend-icon="mdi-creation"
              :disabled="!selectedModel"
              @click="createMarkdownFromLlm"
            >
              {{ $t("kgme_startBuild") }}
            </v-btn>
            <v-btn
              v-else-if="hasIncomplete"
              id="button-resume-build-kgMdEditor"
              color="#C32E0E"
              variant="elevated"
              :prepend-icon="buildHasError ? 'mdi-refresh' : 'mdi-play'"
              @click="runIncomplete"
            >
              {{ buildHasError ? $t("kgme_regenerateErrors") : $t("kgme_resumeBuild") }}
            </v-btn>
            <v-btn
              v-else
              id="button-building-next-kgMdEditor"
              color="#C32E0E"
              variant="elevated"
              append-icon="mdi-arrow-right"
              @click="assembleAndProceed"
            >
              {{ $t("kgme_next") }}
            </v-btn>
          </div>
        </div>

        <!-- Step: Edit Markdown -->
        <div v-else-if="step === 'edit' && !loading">
          <div class="text-subtitle-1 font-weight-medium mb-3">
            <v-icon start color="#C32E0E">mdi-language-markdown-outline</v-icon>{{ $t("kgme_editTitle") }}
          </div>
          <v-textarea
            id="textarea-markdown-kgMdEditor"
            v-model="markdown"
            variant="outlined"
            rows="24"
            no-resize
            class="kg-md-textarea kg-edit-textarea"
            :hint="$t('kgme_editHint')"
            persistent-hint
          />
          <div class="d-flex mt-4">
            <v-btn id="button-edit-back-kgMdEditor" variant="text" prepend-icon="mdi-arrow-left" @click="onBack">{{ $t("kgme_back") }}</v-btn>
            <v-spacer />
            <v-btn id="button-edit-next-kgMdEditor" color="#C32E0E" variant="elevated" append-icon="mdi-arrow-right" @click="goNext">{{ $t("kgme_next") }}</v-btn>
          </div>
        </div>

        <!-- Step: Map Levels -->
        <div v-else-if="step === 'map'">
          <div class="text-subtitle-1 font-weight-medium mb-1">
            <v-icon start color="#C32E0E">mdi-sitemap-outline</v-icon>{{ $t("kgme_mapTitle") }}
          </div>
          <div class="text-caption text-medium-emphasis mb-4">{{ $t("kgme_mapHint") }}</div>
          <!-- S16-P1 P4: data-testid -->
          <v-row v-for="lvl in detectedLevels" :key="lvl.level" :data-testid="`kgme-map-row-${lvl.level}`" align="center" class="mb-1">
            <v-col cols="12" sm="5">
              <span class="font-weight-medium">{{ lvl.label }}</span>
              <span class="text-caption text-medium-emphasis ml-1 text-truncate">"{{ lvl.sample }}"</span>
            </v-col>
            <v-col cols="12" sm="7">
              <v-select
                :id="`select-level-${lvl.level}-kgMdEditor`"
                v-model="levelMap[lvl.level]"
                :items="mapLevelOptions"
                item-title="label"
                item-value="id"
                density="compact"
                variant="outlined"
                hide-details
              >
                <template #selection="{ item }">
                  <span class="kg-node-dot" :class="{ 'kg-node-dot--skip': item.value === '' }" :style="{ backgroundColor: nodeColor(item.value) }"></span>
                  {{ item.title }}
                </template>
                <template #item="{ item, props }">
                  <v-list-item v-bind="props" :title="item.title">
                    <template #prepend>
                      <span class="kg-node-dot" :class="{ 'kg-node-dot--skip': item.value === '' }" :style="{ backgroundColor: nodeColor(item.value) }"></span>
                    </template>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <div class="d-flex mt-4">
            <v-btn id="button-map-back-kgMdEditor" variant="text" prepend-icon="mdi-arrow-left" @click="onBack">{{ $t("kgme_back") }}</v-btn>
            <v-spacer />
            <v-btn id="button-map-next-kgMdEditor" color="#C32E0E" variant="elevated" append-icon="mdi-arrow-right" @click="goNext">{{ $t("kgme_next") }}</v-btn>
          </div>
        </div>

        <!-- The "Import Mode" step used to sit here (Create new nodes / Update existing, match by
             label). It was removed because it could not do anything: ConfirmMarkdownConversion always
             creates a BRAND-NEW graph before the batch import, so there are never any existing nodes
             for "update" to match a label against — both options behaved identically. Worse, picking
             "Update existing" read as "merge into my existing graph", which it never did: a new name
             produced a second graph, a reused name failed with a 409 (S16-P1: duplicate-name check of the bal GraphRAG engine, DC-6). Confirm now sends "create"
             outright. Restoring the choice means giving the user a TARGET graph first — see onConfirm. -->

        <!-- Step: Preview -->
        <div v-else-if="step === 'preview'">
          <div class="d-flex align-center mb-3 flex-wrap">
            <div class="text-subtitle-1 font-weight-medium">
              <v-icon start color="#C32E0E">mdi-graph-outline</v-icon>{{ $t("kgme_previewTitle") }}
            </div>
            <v-spacer />
            <!-- S16-P1 P4: data-testid (kgme-preview-count-* · kgme-preview-empty · kgme-preview-tree · kgme-preview-node-{i}) -->
            <v-chip size="small" color="#C32E0E" variant="tonal" class="mr-2" data-testid="kgme-preview-count-nodes">{{ previewPayload.nodes.length }} {{ $t("kgme_unitNodes") }}</v-chip>
            <v-chip size="small" color="#C32E0E" variant="tonal" data-testid="kgme-preview-count-edges">{{ previewPayload.edges.length }} {{ $t("kgme_unitEdges") }}</v-chip>
          </div>
          <div v-if="previewPayload.nodes.length === 0" class="text-medium-emphasis py-6 text-center" data-testid="kgme-preview-empty">{{ $t("kgme_previewEmpty") }}</div>
          <ul v-else class="kg-preview-tree" data-testid="kgme-preview-tree">
            <li v-for="(node, i) in previewPayload.nodes" :key="node.id" :data-testid="`kgme-preview-node-${i}`" :style="{ marginLeft: node.__depth * 20 + 'px' }">
              <v-chip size="x-small" class="mr-2 font-weight-medium" :style="{ backgroundColor: nodeColor(node.node_type), color: '#1c1c1c' }">{{ node.node_type }}</v-chip>{{ node.label }}
            </li>
          </ul>
          <div class="d-flex mt-4">
            <v-btn id="button-preview-back-kgMdEditor" variant="text" prepend-icon="mdi-arrow-left" :disabled="confirming" @click="onBack">{{ $t("kgme_back") }}</v-btn>
            <v-spacer />
            <v-btn
              id="button-confirm-kgMdEditor"
              color="#C32E0E"
              variant="elevated"
              prepend-icon="mdi-check"
              :loading="confirming"
              :disabled="previewPayload.nodes.length === 0"
              @click="onConfirm"
            >
              {{ $t("kgme_confirm") }}
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Popup: ask whether to use an LLM to shape the markdown. -->
    <v-dialog v-model="askLlmDialog" max-width="480" persistent>
      <!-- S16-P1 P4: data-testid -->
      <v-card class="card2" data-testid="kgme-ask-llm-dialog">
        <v-card-title class="text-subtitle-1 d-flex align-center">
          <v-icon start color="#C32E0E">mdi-robot-happy-outline</v-icon>{{ $t("kgme_askLlmTitle") }}
        </v-card-title>
        <v-card-text>
          {{ $t("kgme_askLlmText") }}
          <div class="text-caption text-medium-emphasis mt-1">{{ $t("kgme_askLlmHint") }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn id="button-llm-no-kgMdEditor" variant="text" @click="proceedWithoutLlm">{{ $t("kgme_askLlmNo") }}</v-btn>
          <v-btn id="button-llm-yes-kgMdEditor" color="#C32E0E" variant="elevated" @click="proceedWithLlm">{{ $t("kgme_askLlmYes") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Popup: generate prompt with AI (type instruction, then Generate) -->
    <v-dialog v-model="genPromptDialog" max-width="560" persistent>
      <!-- S16-P1 P4: data-testid -->
      <v-card class="card2" data-testid="kgme-genprompt-dialog">
        <v-card-title class="text-subtitle-1 d-flex align-center pt-4">
          <v-icon start color="#C32E0E">mdi-auto-fix</v-icon>{{ $t("kgme_genPromptDialogTitle") }}
        </v-card-title>
        <v-card-text>
          <v-textarea
            id="textarea-genprompt-kgMdEditor"
            v-model="genPromptInput"
            :label="$t('kgme_genPromptInputLabel')"
            variant="outlined"
            rows="5"
            auto-grow
            hide-details
            autofocus
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <!-- S16-P1 P4: data-testid -->
          <v-btn variant="text" data-testid="kgme-genprompt-cancel" :disabled="generatingPrompt" @click="genPromptDialog = false">{{ $t("cancel") }}</v-btn>
          <v-btn id="button-genprompt-run-kgMdEditor" color="#C32E0E" variant="elevated" :loading="generatingPrompt" @click="runGeneratePrompt">
            <v-icon start size="small">mdi-auto-fix</v-icon>{{ $t("kgme_generate") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- S16-P1 T5: expand popup — view/edit a prompt/chunk/combined box full-size -->
    <v-dialog v-model="expandDialog" max-width="900" persistent scrollable>
      <!-- S16-P1 P4: data-testid -->
      <v-card class="card2" data-testid="kgme-expand-dialog">
        <v-card-title class="text-subtitle-1 d-flex align-center pt-4">
          <v-icon start color="#C32E0E">mdi-arrow-expand</v-icon>{{ expandTitle }}
        </v-card-title>
        <v-card-text>
          <v-textarea
            id="textarea-expand-kgMdEditor"
            v-model="expandText"
            variant="outlined"
            rows="20"
            no-resize
            hide-details
            autofocus
            class="kg-expand-textarea"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <!-- S16-P1 P4: data-testid -->
          <v-btn variant="text" data-testid="kgme-expand-cancel" @click="closeExpand">{{ $t("cancel") }}</v-btn>
          <v-btn id="button-expand-save-kgMdEditor" color="#C32E0E" variant="elevated" @click="saveExpand">
            <v-icon start size="small">mdi-check</v-icon>{{ $t("save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- S16-P1 T9-2: split-chunk editor — left panel holds the full original chunk, right panel starts empty.
         The user cuts whatever text they want into the right half; both panels are freely editable. -->
    <v-dialog v-model="splitDialog" fullscreen persistent scrollable>
      <!-- S16-P1 P4: data-testid -->
      <v-card class="card2" data-testid="kgme-split-dialog">
        <v-card-title class="text-subtitle-1 d-flex align-center pt-4">
          <v-icon start color="#C32E0E">mdi-call-split</v-icon>{{ $t("kgme_splitTitle") }}
          <span v-if="splitIndex !== null" class="text-caption text-medium-emphasis ml-2">
            {{ $t("kgme_chunkLabel") }} {{ splitIndex + 1 }}
          </span>
        </v-card-title>
        <v-card-text>
          <div class="text-caption text-medium-emphasis mb-3">{{ $t("kgme_splitHint") }}</div>
          <v-row dense>
            <!-- Left: full content of the original chunk -->
            <v-col cols="12" md="6">
              <div class="d-flex align-center mb-1">
                <span class="text-body-2 font-weight-medium">{{ $t("kgme_splitPart1") }}</span>
                <v-spacer />
                <span class="text-caption text-medium-emphasis">{{ splitPart1.length }} {{ $t("kgme_chunkCharsUnit") }}</span>
              </div>
              <!-- S16-P1 P4: data-testid -->
              <v-textarea
                v-model="splitPart1"
                data-testid="kgme-split-part1"
                variant="outlined"
                no-resize
                hide-details
                density="compact"
                class="kg-md-textarea kg-split-textarea"
              />
            </v-col>
            <!-- Right: new chunk — starts empty, user moves text here -->
            <v-col cols="12" md="6">
              <div class="d-flex align-center mb-1">
                <span class="text-body-2 font-weight-medium">{{ $t("kgme_splitPart2") }}</span>
                <v-spacer />
                <span class="text-caption text-medium-emphasis">{{ splitPart2.length }} {{ $t("kgme_chunkCharsUnit") }}</span>
              </div>
              <!-- S16-P1 P4: data-testid -->
              <v-textarea
                v-model="splitPart2"
                data-testid="kgme-split-part2"
                variant="outlined"
                no-resize
                hide-details
                density="compact"
                class="kg-md-textarea kg-split-textarea"
                :placeholder="$t('kgme_splitPart2Placeholder')"
              />
            </v-col>
          </v-row>
          <!-- S16-P1 P4: data-testid -->
          <v-alert
            v-if="!splitPart1.trim() || !splitPart2.trim()"
            data-testid="kgme-split-both-required"
            type="warning"
            variant="tonal"
            density="compact"
            rounded="lg"
            class="mt-3"
          >
            {{ $t("kgme_splitBothRequired") }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <!-- S16-P1 P4: data-testid -->
          <v-btn variant="text" data-testid="kgme-split-cancel" @click="splitDialog = false">{{ $t("cancel") }}</v-btn>
          <v-btn
            id="button-split-confirm-kgMdEditor"
            color="#C32E0E"
            variant="elevated"
            :disabled="!splitPart1.trim() || !splitPart2.trim()"
            @click="confirmSplit"
          >
            <v-icon start size="small">mdi-call-split</v-icon>{{ $t("kgme_splitConfirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- S16-P1 T-new: add-chunk editor — a textarea to type the new chunk's/section's content before inserting -->
    <v-dialog v-model="addDialog" max-width="900" persistent scrollable>
      <!-- S16-P1 P4: data-testid -->
      <v-card class="card2" data-testid="kgme-add-dialog">
        <v-card-title class="text-subtitle-1 d-flex align-center pt-4">
          <v-icon start color="#C32E0E">mdi-note-plus-outline</v-icon>{{ $t("kgme_addChunkTitle") }}
        </v-card-title>
        <v-card-text>
          <div class="text-caption text-medium-emphasis mb-3">{{ $t("kgme_addChunkHint") }}</div>
          <!-- S16-P1 P4: data-testid -->
          <v-textarea
            v-model="addText"
            data-testid="kgme-add-textarea"
            variant="outlined"
            rows="18"
            no-resize
            hide-details
            autofocus
            density="compact"
            class="kg-md-textarea"
            :placeholder="$t('kgme_addChunkPlaceholder')"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <!-- S16-P1 P4: data-testid -->
          <v-btn variant="text" data-testid="kgme-add-cancel" @click="addDialog = false">{{ $t("cancel") }}</v-btn>
          <v-btn
            id="button-add-confirm-kgMdEditor"
            color="#C32E0E"
            variant="elevated"
            :disabled="!addText.trim()"
            @click="confirmAdd"
          >
            <v-icon start size="small">mdi-plus</v-icon>{{ $t("kgme_addChunkConfirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- S16-P1 T9-3: convert-done popup (replaces the old $swal alert) — offer to open the created graph -->
    <v-dialog v-model="convertDoneDialog" max-width="460" persistent>
      <!-- S16-P1 P4: data-testid (kgme-done-dialog · kgme-done-graphname · kgme-done-counts) -->
      <v-card class="card2 kg-done-card" data-testid="kgme-done-dialog">
        <div class="text-center pt-6 px-6">
          <v-icon size="52" color="#2ABF56">mdi-check-circle</v-icon>
          <div class="text-h6 font-weight-medium mt-3">{{ $t("kgl_createSuccess") }}</div>
          <div class="text-body-2 font-weight-medium mt-2" style="word-break: break-word;" data-testid="kgme-done-graphname">{{ graphName }}</div>
          <div class="text-caption text-medium-emphasis mt-1" data-testid="kgme-done-counts">
            {{ createdNodeCount }} {{ $t("kgme_unitNodes") }} · {{ createdEdgeCount }} {{ $t("kgme_unitEdges") }}
          </div>
          <div class="text-body-2 mt-4">{{ $t("kgme_gotoKgText") }}</div>
        </div>
        <v-card-actions class="px-6 pb-6 pt-4 flex-column" style="gap: 8px;">
          <v-btn
            id="button-done-gotokg-kgMdEditor"
            color="#C32E0E"
            variant="elevated"
            block
            append-icon="mdi-arrow-right"
            @click="gotoCreatedGraph"
          >
            {{ $t("kgme_gotoKgYes") }}
          </v-btn>
          <v-btn id="button-done-tolist-kgMdEditor" variant="text" block @click="gotoDocumentList">
            {{ $t("kgme_gotoKgNo") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
// S16-P1: node-type colors — the same hex values the bal GraphRAG engine seeds into the graph schema
// (DocumentConvertGraphPlan.SchemaSeed, DC-5), so the Map Levels UI matches the colors on the graph canvas.
const NODE_TYPE_COLORS = {
  Category: "#FF6B6B",
  Product: "#44AF69",
  ProductType: "#8ABFB0",
  Service: "#F38181",
  Promotion: "#AA96DA",
  Troubleshooting: "#FCBAD3",
  Feature: "#A8D8EA",
  Condition: "#FFE66D",
  Community: "#95E1D3",
  // Document-import heading hierarchy
  Heading1: "#E07B54",
  Heading2: "#E8B86D",
  Heading3: "#67C3B0",
  Heading4: "#9B8EC4",
  Heading5: "#7FB4D4",
  Heading6: "#B8D8A4",
};
const DEFAULT_NODE_COLOR = "#C0C0C0";

// S16-P1: built-in node types of the bal GraphRAG engine (GraphNodeTypes.All in GraphOntology.cs,
// DC-10) — same set, same order. These are always valid in any graph
// (they're the built-in enum, not schema-file entries), so we always offer them in Map Levels
// even when the fetched schema returns few/no types.
const DEFAULT_NODE_TYPES = [
  "Category",
  "ProductType",
  "Product",
  "Service",
  "Promotion",
  "Troubleshooting",
  "Feature",
  "Condition",
  "Community",
  "GraphNode",
  "Heading1",
  "Heading2",
  "Heading3",
  "Heading4",
  "Heading5",
  "Heading6",
];

export default {
  name: "KnowledgeGraphMarkdownEditor",
  data() {
    return {
      vectorDBName: "",
      // Wizard step: "chunks" -> "mode" -> ("llm" -> "building") -> "edit" -> "map" -> "import" -> "preview".
      step: "chunks",
      chunkModeChoice: "BySection", // radio v-model for the mode picker (before Next is clicked)
      chunkMode: "BySection", // "BySection" | "Combined" — the mode actually in effect once chosen
      graphId: null,
      graphName: "", // user-entered name for the created graph (set on the chunks step)
      markdown: "",
      schemaNodeTypes: [],
      levelMap: {},
      loading: false, // true while Prepare / schema fetch is in flight
      confirming: false,

      // Stepper navigation (S16-P1 T9-1): keys of every step the user has reached, so the stepper header can be
      // clicked to jump to any already-visited step (backward OR forward), never to a not-yet-reached one.
      visitedSteps: ["chunks"],

      // Confirm-done popup (S16-P1 T9-3): a custom in-page dialog (replaces the old $swal alert) shown after a
      // successful convert — offers to jump into the created graph's viewer or back to the document list.
      convertDoneDialog: false,
      createdGraphId: null,
      createdNodeCount: 0,
      createdEdgeCount: 0,

      // Existing graph names (lower-cased) — the entered graphName must not duplicate one of these.
      existingGraphNames: [],

      // --- Chunk selection (S16-P1) ---
      allChunks: [], // [{ index, chunkId, subChunkId, content, preview, charCount }]
      selectedChunkIndexes: [], // 0-based indexes the user chose to convert
      loadingChunks: false,

      // Static option lists (labels are i18n keys resolved in the template).
      modeOptions: [
        { value: "BySection", title: "kgme_modeBySection", desc: "kgme_modeBySectionDesc" },
        { value: "Combined", title: "kgme_modeCombined", desc: "kgme_modeCombinedDesc" },
      ],

      // --- LLM-assisted markdown shaping ---
      askLlmDialog: false, // "use AI to build the markdown?" yes/no popup, shown after Next
      useLlm: false, // resolved choice
      llmModels: [], // GetDDLModelData items ({ modelName, modelDisplay, modelType, ... })
      loadingModels: false,
      selectedModel: null, // composite "ModelName|ModelDisplay" value
      generatingPrompt: false,
      llmPrompt: "",
      // Chunks-per-call — user-tunable (BySection only). LLM runs one call at a time (no parallelism).
      llmBatchSize: 2,
      // Output token cap per call — seeded from the selected model's MaxToken, user can raise it so long
      // documents don't get truncated.
      maxTokens: null,
      // Combined mode: the selected document as one or more editable SECTIONS. Starts as a single
      // section (all chunks joined with \n\n = the non-AI Combined assembly). The user can split/add/
      // delete sections; each section is generated as its OWN LLM call, so splitting a too-large
      // document into sections is what actually resolves an input-context overflow in Combined mode.
      combinedSections: [""],

      // --- Generate-prompt popup (S16-P1) ---
      genPromptDialog: false,
      genPromptInput: "",

      // --- Expand popup (view/edit a prompt/chunk box full-size) ---
      expandDialog: false,
      expandTitle: "",
      expandText: "",
      _expandCommit: null, // (value) => void — writes the edited text back to its source

      // --- Split-chunk editor (S16-P1 T9-2): two editable halves the user controls before committing ---
      splitDialog: false,
      splitIndex: null, // chunk index (BySection) or section index (Combined) being split
      splitIsSection: false, // true → splitting a Combined section; false → a BySection chunk
      splitPart1: "",
      splitPart2: "",

      // --- Add-chunk editor: textarea to type a new chunk's/section's content before inserting it ---
      addDialog: false,
      addAfterIndex: null, // insert after this chunk/section index
      addIsSection: false, // true → adding a Combined section; false → a BySection chunk
      addText: "",

      // --- Build progress (S16-P1) ---
      // Each batch groups llmBatchSize chunks and is generated in one GenerateChunkMarkdown call.
      // The build runs inline on the Configure step (right column); failed chunks are edited directly
      // in the left column, so there is no separate build step or edit-chunk dialog.
      batches: [], // [{ id, indexes:[chunkIndex...], status:'pending'|'running'|'done'|'error', markdown, error, live, serverPhase }]
      building: false, // true while any batch calls are in flight
      stopRequested: false, // set by Stop — halts the loop and aborts the in-flight stream
      liveBatchId: null, // id of the batch currently streaming tokens (drives the live preview box)

      // S16-P1: kept identical to the backend DocumentConvertMarkdown.DefaultMarkdownPrompt (BAL/GraphRAG/Ingest/DocumentConvert), which
      // GraphRagDocumentConvertController (api/v0/GraphRagDocumentConvert/GenerateChunkMarkdownStream) uses when the prompt is blank — TC-S16P1-79 checks equality.
      defaultPrompt:
        "คุณเป็นผู้ช่วยผู้เชี่ยวชาญด้านการจัดโครงสร้างเอกสารให้เป็น Markdown เพื่อนำไปสร้าง Knowledge Graph " +
        "โดยแต่ละหัวข้อ (heading) จะถูกแปลงเป็น node หนึ่งตัวในกราฟ และเนื้อหาที่อยู่ใต้หัวข้อนั้นจะกลายเป็นคำอธิบาย (description) ของ node " +
        "จงแปลงเนื้อหาเอกสารดิบต่อไปนี้ให้เป็น Markdown ที่มีโครงสร้างชัดเจนตามหลักการดังนี้:\n\n" +
        "1) โครงสร้างหัวข้อ (heading hierarchy):\n" +
        "   - ใช้ # สำหรับหัวข้อหลักหรือชื่อเรื่องของเอกสาร (ถ้าเนื้อหามีชื่อเรื่องที่ชัดเจน)\n" +
        "   - ใช้ ## สำหรับหมวดหมู่หรือประเด็นหลักแต่ละเรื่อง\n" +
        "   - ใช้ ### หรือลึกกว่า (####, #####) สำหรับหัวข้อย่อยที่แตกออกมาจากหัวข้อหลัก ตามความเหมาะสมของเนื้อหาจริง ไม่ต้องฝืนสร้างระดับที่ไม่มีอยู่จริง\n" +
        "   - แต่ละหัวข้อควรแทนแนวคิด บุคคล เหตุการณ์ หรือประเด็นที่ชัดเจนและแยกออกจากกันได้ ไม่ใช่แค่การจัด format ลอยๆ\n" +
        "   - หลีกเลี่ยงการแตกหัวข้อย่อยเกินความจำเป็นจนเนื้อหากระจัดกระจาย และหลีกเลี่ยงการยัดหลายประเด็นไว้ในหัวข้อเดียวจนอ่านไม่รู้เรื่อง\n\n" +
        "2) เนื้อหาใต้แต่ละหัวข้อ:\n" +
        "   - เขียนเป็นย่อหน้าสั้นๆ กระชับแต่ครบถ้วน เรียบเรียงจากต้นฉบับให้อ่านง่าย โดยคงข้อเท็จจริง ตัวเลข วันที่ ชื่อเฉพาะ และรายละเอียดสำคัญทั้งหมดไว้ครบถ้วน ห้ามตัดข้อมูลที่มีอยู่จริงในต้นฉบับออก\n" +
        "   - หากเนื้อหาต้นฉบับเป็นตารางหรือรายการ ให้แปลงเป็นประโยคหรือ bullet list ที่อ่านง่าย แต่ยังคงข้อมูลไว้ครบถ้วนเหมือนเดิม\n" +
        "   - ห้ามแต่งเติม สมมติ ตีความเกินจริง หรือเพิ่มข้อมูลที่ไม่มีอยู่ในต้นฉบับโดยเด็ดขาด\n\n" +
        "3) ภาษาและคำศัพท์: เขียนด้วยภาษาเดียวกับเอกสารต้นฉบับเสมอ ห้ามแปลเป็นภาษาอื่น และใช้คำศัพท์/ชื่อเฉพาะให้สอดคล้องกันตลอดทั้งเอกสาร\n\n" +
        "4) หากเนื้อหาต้นฉบับพูดถึงเรื่องเดียวกันซ้ำในหลายจุด ให้รวมไว้ใต้หัวข้อเดียวกัน ไม่ต้องสร้างหัวข้อซ้ำหลายอัน\n\n" +
        "5) หากเนื้อหาต้นฉบับไม่มีโครงสร้างหรือหัวข้อชัดเจนเลย ให้ใช้วิจารณญาณแบ่งเนื้อหาออกเป็นหัวข้อตามการเปลี่ยนประเด็นที่สังเกตเห็นได้ตามธรรมชาติ\n\n" +
        "6) ตอบกลับเป็นเนื้อหา Markdown ล้วนๆ เท่านั้น ห้ามใส่คำอธิบาย คำนำ หรือสรุปเพิ่มเติมใดๆ นอกเหนือจาก Markdown และห้ามครอบเนื้อหาด้วย code fence (```)",
    };
  },
  computed: {
    // The full ordered step list shown in the stepper from the very first screen. The Configure/Build
    // steps only appear once the user opts into the AI path (useLlm), so the stepper grows to match.
    wizardSteps() {
      const steps = [
        { key: "chunks", title: this.$t("kgme_stepChunks") },
        { key: "mode", title: this.$t("kgme_stepMode") },
      ];
      if (this.useLlm) {
        // Configure + Build live on one screen now, so there is no separate Build step.
        steps.push({ key: "llm", title: this.$t("kgme_stepConfig") });
      }
      steps.push({ key: "edit", title: this.$t("kgme_stepEdit") });
      steps.push({ key: "map", title: this.$t("kgme_stepMap") });
      // No "import" step — the mode it asked for had no effect (see the template where it lived).
      steps.push({ key: "preview", title: this.$t("kgme_stepPreview") });
      return steps;
    },
    // 1-based index of the current step within wizardSteps — drives the v-stepper highlight.
    currentStepNumber() {
      const idx = this.wizardSteps.findIndex((s) => s.key === this.step);
      return idx === -1 ? 1 : idx + 1;
    },
    // Chunk select-all checkbox state.
    allChunksSelected() {
      return this.allChunks.length > 0 && this.selectedChunkIndexes.length === this.allChunks.length;
    },
    someChunksSelected() {
      return this.selectedChunkIndexes.length > 0 && this.selectedChunkIndexes.length < this.allChunks.length;
    },
    // The selected chunk objects (ordered) — shown/edited in the left column of the Configure step.
    selectedChunkObjects() {
      return this.selectedChunkIndexes
        .slice()
        .sort((a, b) => a - b)
        .map((i) => this.allChunks.find((c) => c.index === i))
        .filter(Boolean);
    },
    // Build progress counters.
    buildHasError() {
      return this.batches.some((b) => b.status === "error");
    },
    // All batches have settled (done or error) — nothing still pending/running.
    buildAllDone() {
      return this.batches.length > 0 && this.batches.every((b) => b.status === "done" || b.status === "error");
    },
    // Progress is counted by CHUNK, not by batch/group — a batch carries indexes[] chunks, so the
    // totals stay stable even when isolate-on-failure splits a group into per-chunk sub-batches.
    chunkTotal() {
      return this.batches.reduce((s, b) => s + (b.indexes ? b.indexes.length : 0), 0);
    },
    chunkDoneCount() {
      return this.batches.reduce((s, b) => s + (b.status === "done" && b.indexes ? b.indexes.length : 0), 0);
    },
    chunkErrorCount() {
      return this.batches.reduce((s, b) => s + (b.status === "error" && b.indexes ? b.indexes.length : 0), 0);
    },
    buildProgressPct() {
      if (!this.chunkTotal) return 0;
      return Math.round(((this.chunkDoneCount + this.chunkErrorCount) / this.chunkTotal) * 100);
    },
    // Continuously-rising overall % — the chunk-based milestones PLUS a live estimate of the chunk(s)
    // currently streaming, so the bar keeps moving during a generation instead of only jumping when a
    // whole chunk settles. The in-progress fraction ≈ streamed length / expected output length, using
    // the input content length as a proxy for the restructured-markdown output length.
    overallProgressPct() {
      const total = this.chunkTotal;
      if (!total) return 0;
      let credit = this.chunkDoneCount + this.chunkErrorCount; // settled units
      this.batches.forEach((b) => {
        if (b.status === "running") {
          const liveLen = (b.live || "").length;
          const expected =
            this.chunkMode === "Combined"
              ? (this.combinedSections[b.indexes[0]] || "").length
              : (b.indexes || []).reduce((s, i) => s + (this.chunkContentForIndex(i) || "").length, 0);
          const f = expected > 0 ? Math.min(0.95, Math.max(0.03, liveLen / expected)) : 0.5;
          credit += f * (b.indexes ? b.indexes.length : 1);
        }
      });
      return Math.min(100, Math.round((credit / total) * 100));
    },
    // Real-time LLM activity for the batch currently streaming. Derives the phase from the stream:
    // request sent but no tokens yet ("reading" = the model is ingesting/analyzing the input), then
    // tokens flowing in ("generating", with a live character count). null when nothing is streaming.
    liveActivity() {
      if (!this.building) return null;
      const lb = this.liveBatch;
      if (!lb) return { phase: "preparing", label: "", chars: 0 };
      const chars = (lb.live || "").length;
      const label = (lb.indexes || []).map((i) => i + 1).join(", ");
      const sp = lb.serverPhase || "";
      // Resolve the phase: finalizing (backend, after generation) > generating (tokens flowing) >
      // retrying (backend, rate-limit backoff before any token) > reading (connected, prefilling input).
      let phase;
      if (sp === "finalizing") phase = "finalizing";
      else if (chars > 0) phase = "generating";
      else if (sp === "retrying") phase = "retrying";
      else phase = "reading";
      return { phase, label, chars };
    },
    // Icon that mirrors the current LLM phase, so the phase is visible at a glance under the bar.
    buildStatusIcon() {
      const a = this.liveActivity;
      if (!a) return "mdi-loading";
      return (
        {
          reading: "mdi-text-box-search-outline",
          generating: "mdi-creation",
          retrying: "mdi-refresh",
          finalizing: "mdi-broom",
          preparing: "mdi-loading",
        }[a.phase] || "mdi-loading"
      );
    },
    // Spinner for preparing/retrying (rotating), gentle pulse for reading/generating/finalizing.
    buildStatusIconClass() {
      const a = this.liveActivity;
      if (!a || a.phase === "preparing" || a.phase === "retrying") return "mdi-spin";
      return "kg-pulse";
    },
    // Human-readable "what is happening right now" line shown under the progress bar — reflects the
    // LLM's live phase (reading the input vs. generating, with a running character count).
    buildStatusText() {
      if (this.building) {
        const a = this.liveActivity;
        if (!a || a.phase === "preparing") return this.$t("kgme_statusPreparing");
        const combined = this.chunkMode === "Combined";
        const charsSuffix = " · " + a.chars.toLocaleString() + " " + this.$t("kgme_chunkCharsUnit");
        if (a.phase === "retrying") return this.$t("kgme_statusRetrying");
        if (a.phase === "finalizing") return this.$t("kgme_statusFinalizing");
        if (a.phase === "reading") {
          return combined
            ? this.$t("kgme_statusReadingDoc")
            : this.$t("kgme_statusReadingChunk") + " " + a.label;
        }
        // generating
        return combined
          ? this.$t("kgme_statusGenDoc") + charsSuffix
          : this.$t("kgme_statusGenChunk") + " " + a.label + charsSuffix;
      }
      if (this.buildAllDone && !this.buildHasError) return this.$t("kgme_statusDone");
      if (this.buildHasError) return this.$t("kgme_statusHasError");
      if (this.hasIncomplete) return this.$t("kgme_statusPaused");
      return "";
    },
    // Any chunk not yet done — pending (e.g. left by a Stop) or errored. Drives the resume/regenerate button.
    hasIncomplete() {
      return this.batches.some((b) => b.status !== "done");
    },
    // True when the entered graph name duplicates an existing graph (case-insensitive) → block Next.
    graphNameTaken() {
      const n = (this.graphName || "").trim().toLowerCase();
      return !!n && this.existingGraphNames.includes(n);
    },
    // The batch currently streaming tokens (drives the realtime live-preview box).
    liveBatch() {
      if (this.liveBatchId == null) return null;
      return this.batches.find((b) => b.id === this.liveBatchId) || null;
    },
    // Map Levels options = the built-in GraphRAG node types (S16-P1) (canonical order) first — using the fetched
    // schema's own id/label when it has them — then any extra types the schema returns that aren't
    // built-in. This guarantees Product/Heading1.. are always selectable regardless of what the
    // schema fetch returned. (Excludes the Skip sentinel — see mapLevelOptions.)
    schemaNodeTypeOptions() {
      const norm = (s) => String(s || "").toLowerCase().replace(/[\s_-]+/g, "");
      const schema = (this.schemaNodeTypes || []).map((t) => ({ id: t.id, label: t.label || t.id }));
      const schemaByNorm = new Map(schema.map((o) => [norm(o.id), o]));
      const options = [];
      const used = new Set();
      DEFAULT_NODE_TYPES.forEach((id) => {
        const hit = schemaByNorm.get(norm(id));
        options.push(hit || { id, label: id });
        used.add(norm(id));
      });
      schema.forEach((o) => {
        if (!used.has(norm(o.id))) {
          options.push(o);
          used.add(norm(o.id));
        }
      });
      return options;
    },
    // The select items shown in Map Levels: a leading "— Skip —" sentinel (id "") + the real node
    // types. Skip is kept OUT of schemaNodeTypeOptions so the default-picker never lands on it.
    mapLevelOptions() {
      return [{ id: "", label: this.$t("kgme_skip") }, ...this.schemaNodeTypeOptions];
    },
    // Levels that get mapped to a node type. Level 0 is always the document root; levels 1..6 are the
    // markdown heading depths actually present in the current text.
    detectedLevels() {
      const levels = new Map();
      const lines = (this.markdown || "").split("\n");
      for (const line of lines) {
        const m = /^(#{1,6})\s+(.+)$/.exec(line.trim());
        if (m) {
          const level = m[1].length;
          if (!levels.has(level)) levels.set(level, m[2].trim());
        }
      }
      const result = [{ level: 0, label: this.$t("kgme_document"), sample: this.vectorDBName || "" }];
      Array.from(levels.keys())
        .sort((a, b) => a - b)
        .forEach((level) => {
          result.push({ level, label: `Heading ${level} (H${level})`, sample: levels.get(level) });
        });
      return result;
    },
    // Same parse used for both the live Preview panel and the exact Confirm payload.
    previewPayload() {
      return this.parseMarkdownToGraph(this.markdown, this.levelMap);
    },
  },
  watch: {
    // S16-P1 T9-1: remember every step the user reaches (by key, since the step list grows when useLlm turns
    // on) so the stepper header can navigate to any visited step.
    step(val) {
      if (!this.visitedSteps.includes(val)) {
        this.visitedSteps.push(val);
      }
    },
    // Seed the output token cap from the chosen model's configured MaxToken (from the LLM catalog),
    // so the default matches real config; the user can still raise it. Falls back to 4096 when the
    // model has no MaxToken set.
    selectedModel(val) {
      const m = this.llmModels.find((x) => x.modelName === val);
      this.maxTokens = m && m.maxToken ? m.maxToken : this.maxTokens || 4096;
    },
    detectedLevels: {
      immediate: true,
      handler(levels) {
        // S16-P1: default each level to the standard document-import convention (
        // {"0":"Product","1":"Heading1","2":"Heading2"}). The document root (level 0) → Product,
        // and each heading depth Hn → Heading{n}. Match the schema's node types tolerantly (case/
        // space/underscore-insensitive) so we never fall back to an unrelated type when the schema
        // id casing differs slightly. Never auto-defaults to Skip.
        levels.forEach((lvl) => {
          if (this.levelMap[lvl.level] === undefined) {
            const desired = this.defaultNodeTypeForLevel(lvl.level);
            const pick =
              this.resolveSchemaId(desired) ||
              (this.schemaNodeTypeOptions[0] ? this.schemaNodeTypeOptions[0].id : desired);
            this.$set ? this.$set(this.levelMap, lvl.level, pick) : (this.levelMap[lvl.level] = pick);
          }
        });
      },
    },
  },
  mounted() {
    this.vectorDBName = this.$route.query.vectorDBName || "";
    if (!this.vectorDBName) {
      this.showError(this.$t("kgme_missingDoc"));
      this.$router.push({ name: "VectorManagement" });
      return;
    }
    this.graphName = this.vectorDBName; // default graph name = document name; user can rename
    this.step = "chunks";
    this.loadChunks();
    this.loadGraphNames();
  },
  methods: {
    // S16-P1: node-type default for a level: the document root (0) → Product; each heading depth Hn →
    // Heading{n} (H1→Heading1, H2→Heading2). Clamped at Heading6.
    defaultNodeTypeForLevel(level) {
      if (level === 0) {
        return "Product";
      }
      return "Heading" + Math.min(level, 6);
    },
    // Find the schema node-type id that matches `desired`, ignoring case / spaces / _ / - differences.
    resolveSchemaId(desired) {
      const norm = (s) => String(s || "").toLowerCase().replace(/[\s_-]+/g, "");
      const target = norm(desired);
      const hit = this.schemaNodeTypeOptions.find((o) => norm(o.id) === target);
      return hit ? hit.id : null;
    },
    // Color for a node type — matches the GraphRAG graph canvas (S16-P1). The Skip sentinel ("") and unknown types
    // fall back to a neutral colour (the Skip dot is rendered hollow via .kg-node-dot--skip).
    nodeColor(id) {
      if (id === "" || id === null || id === undefined) {
        return "transparent";
      }
      return NODE_TYPE_COLORS[id] || DEFAULT_NODE_COLOR;
    },
    // S16-P1: server message if any, else the step's i18n fallback (D16: an unexpected server error comes
    // back as status 2 with an empty message, so the fallback is what the user sees).
    resolveErrorMessage(res, fallbackKey) {
      return (res && res.message) || this.$t(fallbackKey);
    },
    showError(title) {
      this.$swal.fire({
        icon: "error",
        title,
        showConfirmButton: true,
        confirmButtonText: this.$t("gotIt"),
        confirmButtonColor: "#d33",
      });
    },

    // ---- Chunk selection ----------------------------------------------------
    loadChunks() {
      const self = this;
      self.loadingChunks = true;
      self.$call.GetDocumentChunks(
        { VectorDBName: self.vectorDBName },
        (res) => {
          self.loadingChunks = false;
          if (!res || res.status !== 0) {
            self.showError(self.resolveErrorMessage(res, "kgme_loadChunksFailed"));
            self.$router.push({ name: "VectorManagement" });
            return;
          }
          self.allChunks = (res.data && res.data.chunks) || [];
          // Default: everything selected.
          self.selectedChunkIndexes = self.allChunks.map((c) => c.index);
        },
        () => {
          self.loadingChunks = false;
          self.showError(self.$t("kgme_loadChunksFailed"));
          self.$router.push({ name: "VectorManagement" });
        }
      );
    },
    toggleChunk(index) {
      const i = this.selectedChunkIndexes.indexOf(index);
      if (i === -1) this.selectedChunkIndexes.push(index);
      else this.selectedChunkIndexes.splice(i, 1);
    },
    toggleSelectAll(val) {
      this.selectedChunkIndexes = val ? this.allChunks.map((c) => c.index) : [];
    },
    // Load existing graph names so the naming field can reject duplicates.
    loadGraphNames() {
      const self = this;
      self.$call.ListKnowledgeGraphs(
        (res) => {
          const rows = (res && res.data) || [];
          self.existingGraphNames = rows.map((g) => (g.name || "").trim().toLowerCase()).filter(Boolean);
        },
        () => {
          self.existingGraphNames = []; // non-fatal: fall back to no client-side dup check
        }
      );
    },
    goFromChunks() {
      if (!this.selectedChunkIndexes.length || !this.graphName.trim() || this.graphNameTaken) return;
      this.step = "mode";
    },

    // ---- Mode + ask-LLM -----------------------------------------------------
    chooseMode(mode) {
      // Record the mode, then ask whether to use an LLM before preparing the graph. Nothing is
      // created in the GraphRAG engine until Confirm (S16-P1), so backing out of the popup/steps needs no cleanup.
      this.chunkMode = mode;
      this.askLlmDialog = true;
    },
    // Popup answer "No" — deterministic assembly, straight to the Edit Markdown step.
    proceedWithoutLlm() {
      this.askLlmDialog = false;
      this.useLlm = false;
      // Re-committing the mode restarts the downstream flow — drop any steps reached under a previous
      // choice so the stepper can't jump ahead (e.g. AI path's "edit" leaking into the non-AI path).
      this.visitedSteps = ["chunks", "mode"];
      this.step = "edit";
      this.load();
    },
    // Popup answer "Yes" — go to the config-model + prompt step.
    proceedWithLlm() {
      this.askLlmDialog = false;
      this.useLlm = true;
      // Reset downstream progress: with AI, "edit" is only reachable after generation, so an earlier
      // non-AI visit to "edit" must not stay clickable in the stepper.
      this.visitedSteps = ["chunks", "mode"];
      this.step = "llm";
      this.batches = []; // fresh build state each time we enter Configure
      if (this.chunkMode === "Combined") {
        // Seed a single section with the same join the non-AI Combined path uses. The user can split it
        // into multiple sections (each its own LLM call) from the Configure step.
        this.combinedSections = [this.selectedChunkObjects.map((c) => c.content || "").join("\n\n")];
      }
      if (!this.llmPrompt) {
        this.llmPrompt = this.defaultPrompt;
      }
      if (this.llmModels.length === 0) {
        this.loadModels();
      }
    },
    loadModels() {
      const self = this;
      self.loadingModels = true;
      self.$call.GetDDLModelData(
        (res) => {
          self.loadingModels = false;
          if (res && res.status === 0) {
            // Keep text models only — a text_to_image model can't shape markdown.
            self.llmModels = (res.data || []).filter(
              (m) => !m.modelType || m.modelType.toLowerCase().indexOf("image") === -1
            );
          }
        },
        () => {
          self.loadingModels = false;
        }
      );
    },

    // ---- Generate-prompt popup ---------------------------------------------
    openGenPromptDialog() {
      if (!this.selectedModel) return;
      this.genPromptInput = ""; // always open blank — no default/current prompt prefilled
      this.genPromptDialog = true;
    },
    runGeneratePrompt() {
      const self = this;
      if (!self.selectedModel) return;
      self.generatingPrompt = true;
      self.$call.GenerateKnowledgeGraphMarkdownPrompt(
        // Refine what the user typed in the popup only — not document-aware by design.
        { ModelName: self.selectedModel, Instruction: self.genPromptInput },
        (res) => {
          self.generatingPrompt = false;
          if (res && res.status === 0 && res.data) {
            self.llmPrompt = res.data; // decision 3A — replace the prompt
            self.genPromptDialog = false;
          } else {
            self.showError((res && res.message) || self.$t("kgme_generatePromptFailed"));
          }
        },
        () => {
          self.generatingPrompt = false;
          self.showError(self.$t("kgme_generatePromptFailed"));
        }
      );
    },

    // ---- LLM build orchestration (realtime, per-batch) ----------------------
    // "Start building Markdown" — group the selected chunks into batches and run them.
    createMarkdownFromLlm() {
      if (!this.selectedModel) return;
      this.startBuild();
    },
    chunkContentForIndex(index) {
      const c = this.allChunks.find((x) => x.index === index);
      return c ? c.content : "";
    },
    startBuild() {
      let batches;
      if (this.chunkMode === "Combined") {
        // One batch per non-empty section → one LLM call each. A single section = the whole document in
        // one call (original Combined behaviour); multiple sections fit a large document under context.
        const secs = this.combinedSections
          .map((s, i) => ({ i, content: (s || "").trim() }))
          .filter((x) => x.content !== "");
        if (!secs.length) {
          this.showError(this.$t("kgme_noContentToBuild"));
          return;
        }
        batches = secs.map((x) => ({ id: "cb" + x.i, indexes: [x.i], status: "pending", markdown: "", error: "", live: "", serverPhase: "" }));
      } else {
        // Skip chunks with no content — they are not sent to the LLM and produce no error; the build
        // just moves on to the next chunk.
        const chosen = this.selectedChunkIndexes
          .slice()
          .sort((a, b) => a - b)
          .filter((i) => (this.chunkContentForIndex(i) || "").trim() !== "");
        if (!chosen.length) {
          this.showError(this.$t("kgme_noContentToBuild"));
          return;
        }
        const size = Math.max(1, Math.min(20, parseInt(this.llmBatchSize, 10) || 1));
        batches = [];
        for (let i = 0; i < chosen.length; i += size) {
          batches.push({ id: "b" + i, indexes: chosen.slice(i, i + size), status: "pending", markdown: "", error: "", live: "", serverPhase: "" });
        }
      }
      this.batches = batches;
      // Run over the REACTIVE proxies (this.batches.slice()), not the raw local array — mutating the raw
      // objects bypasses Vue's proxy setter, so the progress computeds never recompute (stale 0/N).
      this.runBatches(this.batches.slice());
    },
    // Run the units one at a time (single LLM, no parallelism) — never abort on error (settle every unit
    // so the full pass/fail picture is visible), but DO stop early if the user hit Stop.
    async runBatches(list) {
      this.building = true;
      this.stopRequested = false;
      for (const b of list) {
        if (this.stopRequested) break;
        await this.runBatch(b);
        if (this.stopRequested) break;
        // BySection isolate-on-failure: one LLM call over the joined chunks returns a single pass/fail,
        // so a multi-chunk batch that errors can't say WHICH chunk broke. Split it into per-chunk
        // sub-batches and run each to pinpoint the real culprit — chunks that pass alone go green, only
        // the truly broken one stays red. (Also recovers a batch that only failed because the joined
        // text overflowed the model's context.) Happy path is untouched: no split unless a batch fails.
        if (b.status === "error" && b.indexes.length > 1 && this.chunkMode !== "Combined") {
          const subs = b.indexes.map((i) => ({
            id: b.id + "_" + i,
            indexes: [i],
            status: "pending",
            markdown: "",
            error: "",
            live: "",
          }));
          const pos = this.batches.indexOf(b);
          if (pos >= 0) this.batches.splice(pos, 1, ...subs);
          // Run the reactive proxies (re-read from this.batches), not the raw sub objects.
          const reactiveSubs = pos >= 0 ? this.batches.slice(pos, pos + subs.length) : subs;
          for (const s of reactiveSubs) {
            if (this.stopRequested) break;
            await this.runBatch(s);
          }
        }
      }
      this.building = false;
      // Auto-advance only when the user didn't stop AND every unit succeeded.
      if (!this.stopRequested && this.buildAllDone && !this.buildHasError) {
        this.assembleAndProceed();
      }
    },
    runBatch(b) {
      const self = this;
      return new Promise((resolve) => {
        b.status = "running";
        b.error = "";
        b.live = ""; // live token buffer, filled as the LLM streams
        b.serverPhase = ""; // backend-reported lifecycle phase (e.g. "retrying", "finalizing")
        self.liveBatchId = b.id; // marks which batch drives the live preview box
        self._livePending = ""; // tokens buffered since the last animation-frame flush
        const isCombined = self.chunkMode === "Combined";
        // Combined+single section = the WHOLE document (Combined:true, no section rule). Combined split
        // into multiple sections, or BySection, sends a PART → Combined:false so the backend appends the
        // "this is a section of a larger doc" rule.
        const combined = isCombined && self.combinedSections.length <= 1;
        const items = isCombined
          ? [{ index: 0, content: self.combinedSections[b.indexes[0]] || "" }]
          : b.indexes.map((i) => ({ index: i, content: self.chunkContentForIndex(i) }));
        let finished = false;
        // Flush buffered tokens to the DOM (called at most once per animation frame).
        const flushLive = () => {
          self._liveRaf = null;
          if (self._livePending) {
            b.live = (b.live || "") + self._livePending;
            self._livePending = "";
            self.scrollLiveToBottom();
          }
        };
        const finish = () => {
          if (finished) return;
          finished = true;
          if (self._liveRaf) {
            cancelAnimationFrame(self._liveRaf);
            self._liveRaf = null;
          }
          if (self._livePending) {
            b.live = (b.live || "") + self._livePending;
            self._livePending = "";
          }
          if (self.liveBatchId === b.id) self.liveBatchId = null;
          self._activeController = null;
          self._activeFinish = null;
          resolve();
        };
        // Stop handler for THIS batch (invoked by stopBuild): revert to pending so it can be re-run.
        self._activeFinish = () => {
          b.status = "pending";
          b.live = "";
          self._livePending = "";
          finish();
        };
        // Realtime token streaming (SSE): BUFFER tokens and flush to the DOM once per animation frame
        // (≤ ~60fps) instead of on every token — avoids O(n²) re-render/scroll thrash on long documents.
        self._activeController = self.$call.GenerateChunkMarkdownStream(
          { ModelName: self.selectedModel, Prompt: self.llmPrompt, Items: items, Combined: combined, MaxTokens: self.maxTokens },
          (token) => {
            self._livePending += token;
            if (!self._liveRaf) self._liveRaf = requestAnimationFrame(flushLive);
          },
          (markdown) => {
            b.markdown = markdown || ((b.live || "") + self._livePending) || "";
            b.status = "done";
            finish();
          },
          (msg) => {
            b.status = "error";
            b.error = msg || self.$t("kgme_buildFailed");
            finish();
          },
          (status) => {
            // Backend lifecycle phase (retrying / finalizing) the frontend can't derive from tokens.
            b.serverPhase = status || "";
          }
        );
      });
    },
    // User pressed Stop: halt the loop, abort the in-flight stream, and settle the running batch. The
    // aborted fetch won't call onDone/onError (call.js swallows AbortError), so we settle it manually.
    stopBuild() {
      this.stopRequested = true;
      if (this._activeController) {
        try {
          this._activeController.abort();
        } catch (e) {
          /* ignore */
        }
      }
      if (this._activeFinish) this._activeFinish();
    },
    scrollLiveToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.liveBox;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    regenerateErrors() {
      const errs = this.batches.filter((b) => b.status === "error");
      if (!errs.length) return;
      this.runBatches(errs);
    },
    // Run every batch that isn't done yet — pending ones left by a Stop plus any errors. Lets the user
    // edit the left column / config after stopping, then resume/finish without re-running done chunks.
    runIncomplete() {
      const todo = this.batches.filter((b) => b.status !== "done");
      if (!todo.length) return;
      this.runBatches(todo);
    },
    retryBatch(b) {
      this.runBatches([b]);
    },
    // S16-P1 T4: edited a chunk's source after it was built → invalidate ONLY that chunk's batch (revert to
    // pending, drop its stale markdown) so the user must regenerate before proceeding. hasIncomplete
    // then flips the primary button back to Build/Resume and disables Next.
    onChunkEdited(index) {
      if (!this.batches.length || this.building) return;
      const b = this.batches.find((x) => x.indexes && x.indexes.includes(index));
      if (b && b.status !== "pending" && b.status !== "running") {
        b.status = "pending";
        b.markdown = "";
        b.error = "";
        b.live = "";
      }
    },
    // Combined mode has a single batch over the whole document → any edit invalidates it.
    // Combined: edited a section's text after it was built → invalidate ONLY that section's batch.
    onCombinedSectionEdited(index) {
      if (!this.batches.length || this.building) return;
      const b = this.batches.find((x) => x.indexes && x.indexes.includes(index));
      if (b && b.status !== "pending" && b.status !== "running") {
        b.status = "pending";
        b.markdown = "";
        b.error = "";
        b.live = "";
        b.serverPhase = "";
      }
    },
    // S16-P1 T5: open a big popup to view/edit a prompt/chunk box full-size. commit(value) writes the edited
    // text back to its source (and, for chunks, triggers the T4 invalidation).
    openExpand(title, value, commit) {
      this.expandTitle = title;
      this.expandText = value || "";
      this._expandCommit = commit;
      this.expandDialog = true;
    },
    saveExpand() {
      if (this._expandCommit) this._expandCommit(this.expandText);
      this.closeExpand();
    },
    closeExpand() {
      this.expandDialog = false;
      this._expandCommit = null;
      this.expandText = "";
      this.expandTitle = "";
    },
    // Concatenate the batch outputs in chunk order into one markdown doc, then fetch the schema and
    // move to the Edit Markdown step.
    assembleAndProceed() {
      if (this.building || this.buildHasError || !this.buildAllDone) return;
      const ordered = this.batches.slice().sort((a, b) => (a.indexes[0] || 0) - (b.indexes[0] || 0));
      const md = ordered
        .map((b) => (b.markdown || "").trim())
        .filter(Boolean)
        .join("\n\n");
      this.markdown = md ? md.trimEnd() + "\n" : "";
      this.loadSchemaThenEdit();
    },
    loadSchemaThenEdit() {
      const self = this;
      self.loading = true;
      self.$call.GetKnowledgeGraphConversionSchema(
        { VectorDBName: self.vectorDBName },
        (res) => {
          self.loading = false;
          if (!res || res.status !== 0) {
            self.onSchemaLoadFailed(self.resolveErrorMessage(res, "kgme_loadSchemaFailed"));
            return;
          }
          self.schemaNodeTypes = (res.data && res.data.schemaNodeTypes) || [];
          self.step = "edit";
        },
        () => {
          self.loading = false;
          self.onSchemaLoadFailed("");
        }
      );
    },
    // This fetch is NOT load-bearing: schemaNodeTypeOptions always seeds the 16 built-in node types (S16-P1: GraphNodeTypes.All)
    // and only overlays the fetched schema onto the ones that match, so Map Levels is fully usable
    // with an empty schema. Stranding a FINISHED AI build behind a schema-fetch hiccup (S16-P1) therefore costs the
    // user every generated chunk for no gain — offer to carry on with the built-ins instead.
    // Declining just closes the dialog: the build is settled, so the primary button is still "Next",
    // which re-runs assembleAndProceed → loadSchemaThenEdit as the retry. Nothing is lost either way
    // (assembleAndProceed writes this.markdown BEFORE calling here, and the batches keep their output).
    onSchemaLoadFailed(message) {
      const self = this;
      self.$swal
        .fire({
          title: self.$t("kgme_loadSchemaFailed"),
          text: [message, self.$t("kgme_schemaFallbackText")].filter(Boolean).join("\n\n"),
          icon: "warning",
          width: "33em",
          showCancelButton: true,
          confirmButtonColor: "#C32E0E",
          cancelButtonColor: "#d33",
          confirmButtonText: self.$t("kgme_schemaContinueDefaults"),
          cancelButtonText: self.$t("cancel"),
        })
        .then((result) => {
          if (!result.isConfirmed) return; // stay put — Next retries
          self.schemaNodeTypes = []; // built-in types only (see schemaNodeTypeOptions)
          self.step = "edit";
        });
    },
    // Per-chunk build status — derived from the batch that contains the chunk. Drives the status icon
    // / error shown under each editable chunk in the Configure step's left column. (A batch may group
    // several chunks, so every chunk in a failed batch shows that batch's error.)
    chunkBatch(index) {
      return this.batches.find((b) => b.indexes.includes(index)) || null;
    },
    chunkStatus(index) {
      const b = this.chunkBatch(index);
      return b ? b.status : "";
    },
    chunkError(index) {
      const b = this.chunkBatch(index);
      return b ? b.error : "";
    },
    // Generic status → icon/color (used by both the per-chunk cards and the single Combined box).
    statusIcon(status) {
      return (
        { pending: "mdi-clock-outline", running: "mdi-loading", done: "mdi-check-circle", error: "mdi-alert-circle" }[
          status
        ] || "mdi-clock-outline"
      );
    },
    statusColor(status) {
      return { pending: "grey", running: "#C32E0E", done: "#2ABF56", error: "#d33" }[status] || "grey";
    },

    // ---- Non-LLM deterministic path ----------------------------------------
    load() {
      const self = this;
      self.loading = true;
      self.$call.PrepareMarkdownConversion(
        {
          VectorDBName: self.vectorDBName,
          ChunkMode: self.chunkMode,
          UseLlm: false,
          ModelName: null,
          Prompt: null,
          SelectedChunkIndexes: self.selectedChunkIndexes,
        },
        (res) => {
          self.loading = false;
          if (!res || res.status !== 0) {
            self.showError(self.resolveErrorMessage(res, "kgme_prepareFailed"));
            self.$router.push({ name: "VectorManagement" });
            return;
          }
          const d = res.data || {};
          self.graphId = d.graphId;
          self.markdown = d.markdown || "";
          self.schemaNodeTypes = d.schemaNodeTypes || [];
        },
        () => {
          self.loading = false;
          self.showError(self.$t("kgme_prepareFailed"));
          self.$router.push({ name: "VectorManagement" });
        }
      );
    },
    goNext() {
      if (this.step === "edit") {
        this.step = "map";
      } else if (this.step === "map") {
        this.step = "preview";
      }
    },
    slugify(text, usedIds) {
      // ASCII-only slug — label carries the real (possibly Thai) display text, the id just
      // needs to be a unique, JSON/URL-safe string. Non-ASCII input collapses to "node".
      let base =
        (text || "node")
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
          .slice(0, 40) || "node";
      let id = base;
      let n = 1;
      while (usedIds.has(id)) {
        id = `${base}-${n}`;
        n += 1;
      }
      usedIds.add(id);
      return id;
    },
    // Pure parse: markdown text + level->node_type map -> {nodes, edges} in the exact shape
    // ConfirmMarkdownConversion expects (S16-P1: bal GraphRAG engine, D8). The document (vectorDBName) is level 0; every H1
    // heading hangs off it, H2 off H1, etc. Parent/child headings become "belongs_to" edges.
    //
    // Skip support (S16-P1: Map Levels "— Skip —"): a level whose node_type is "" creates NO
    // node — its content is folded into the nearest non-skipped ancestor's attributes (keyed by the
    // heading text) and its children re-parent directly onto that ancestor.
    parseMarkdownToGraph(markdown, levelMap) {
      const lines = (markdown || "").split("\n");

      // ── Pass 1: flatten the document into heading records (root = synthetic level 0) ──
      const items = [];
      const rootItem = { level: 0, label: this.vectorDBName || "Document", content: [], isRoot: true };
      items.push(rootItem);
      let cur = rootItem;
      for (const raw of lines) {
        const m = /^(#{1,6})\s+(.+)$/.exec(raw.trim());
        if (m) {
          cur = { level: m[1].length, label: m[2].trim(), content: [], isRoot: false };
          items.push(cur);
        } else {
          cur.content.push(raw);
        }
      }

      // ── Pass 2: build nodes/edges with skip + re-parent ──
      const usedIds = new Set();
      const nodes = [];
      const edges = [];
      const isSkip = (lvl) => levelMap[lvl] === "";
      // Stack of ancestor frames in increasing heading level. A skipped heading pushes a frame with
      // createdNode = null so deeper headings resolve their parent to the nearest ancestor that
      // actually produced a node.
      const stack = []; // { level, createdNode | null }

      for (const it of items) {
        while (stack.length && stack[stack.length - 1].level >= it.level) stack.pop();

        // Nearest ancestor that produced a real node (for parenting / content bubbling).
        let ancestor = null;
        for (let i = stack.length - 1; i >= 0; i--) {
          if (stack[i].createdNode) {
            ancestor = stack[i].createdNode;
            break;
          }
        }
        const content = it.content.join("\n").trim();

        if (isSkip(it.level)) {
          if (ancestor && content) {
            ancestor.attributes[it.label] = ancestor.attributes[it.label]
              ? ancestor.attributes[it.label] + "\n\n" + content
              : content;
          }
          stack.push({ level: it.level, createdNode: null });
          continue;
        }

        // Depth for the preview tree = number of created ancestors above this node.
        let depth = 0;
        for (const f of stack) {
          if (f.createdNode) depth += 1;
        }
        const id = this.slugify(it.isRoot ? this.vectorDBName || "document" : it.label, usedIds);
        const node = {
          id,
          node_type: levelMap[it.level] !== undefined ? levelMap[it.level] : null,
          label: it.label,
          description: content,
          attributes: {},
          __depth: depth,
        };
        nodes.push(node);
        if (ancestor) {
          edges.push({ source: id, target: ancestor.id, relation: "belongs_to", bidirectional: false });
        }
        stack.push({ level: it.level, createdNode: node });
      }

      return { nodes, edges };
    },
    onConfirm() {
      const self = this;
      self.confirming = true;
      const payload = self.previewPayload;
      self.$call.ConfirmMarkdownConversion(
        {
          // Graph is created server-side on confirm (deferred from Prepare) — no GraphId sent.
          VectorDBName: self.vectorDBName,
          GraphName: self.graphName,
          Nodes: payload.nodes.map(({ __depth, ...n }) => n),
          Edges: payload.edges,
          // Always "create": the graph this batch lands in was created empty moments earlier by
          // ConfirmMarkdownConversion, so "update" (match by label) has nothing to match and behaves
          // identically. Sending it as a constant rather than leaving it blank keeps the wire shape
          // unchanged for the backend, which reads this field. Making the choice real again needs a
          // target-graph picker plus skipping the create on the server — not just this line back.
          ImportMode: "create",
        },
        (res) => {
          self.confirming = false;
          if (!res || res.status !== 0) {
            self.showError(self.resolveErrorMessage(res, "kgme_createFailed"));
            return;
          }
          // S16-P1 T7/T9-3 (D4): offer to jump straight into the created graph in the bal Graph Editor (route KnowledgeGraphDetail, params { graphKey };
          // res.data.graphId carries the graphKey) instead of returning to the Document List — shown via a custom in-page dialog (not a $swal alert).
          self.createdGraphId = (res.data && res.data.graphId) || null;
          self.createdNodeCount = (res.data && res.data.nodeCount) || 0;
          self.createdEdgeCount = (res.data && res.data.edgeCount) || 0;
          self.convertDoneDialog = true;
        },
        () => {
          self.confirming = false;
          self.showError(self.$t("kgme_createFailed"));
        }
      );
    },
    // S16-P1 T9-1: click a step in the stepper header to jump straight to it — allowed for any step the user
    // has already visited (backward OR forward), never a not-yet-reached one, and never mid-build.
    onStepClick(stepNumber) {
      if (this.building) return;
      const target = this.wizardSteps[stepNumber - 1];
      if (!target || target.key === this.step) return; // ignore the current step
      if (!this.visitedSteps.includes(target.key)) return; // only steps already reached
      this.step = target.key;
    },
    // S16-P1 T9-2: open the split editor for a chunk. No auto split point — the left panel holds the full
    // original content and the right panel starts empty; the user cuts whatever they want into the
    // right half. confirmSplit does the actual split from whatever the two panels end up holding.
    splitChunk(index) {
      if (this.building) return;
      const c = this.allChunks.find((x) => x.index === index);
      if (!c) return;
      const content = (c.content || "").trim();
      if (content.length < 2) {
        this.showError(this.$t("kgme_splitTooSmall"));
        return;
      }
      this.splitIndex = c.index;
      this.splitIsSection = false;
      this.splitPart1 = content; // left: full original chunk
      this.splitPart2 = ""; // right: empty — user moves text here to form the new chunk
      this.splitDialog = true;
    },
    // --- Combined-mode section operations (mirror the chunk ops but on the combinedSections array;
    // each section is its own LLM call, so splitting/adding creates/removes generate units) ----------
    // Open the split editor for a Combined section.
    splitSection(index) {
      if (this.building) return;
      const content = (this.combinedSections[index] || "").trim();
      if (content.length < 2) {
        this.showError(this.$t("kgme_splitTooSmall"));
        return;
      }
      this.splitIndex = index;
      this.splitIsSection = true;
      this.splitPart1 = content;
      this.splitPart2 = "";
      this.splitDialog = true;
    },
    // Open the "add section" editor (Combined) — a textarea to type the new section's content.
    addSection(index) {
      if (this.building) return;
      this.addAfterIndex = index;
      this.addIsSection = true;
      this.addText = "";
      this.addDialog = true;
    },
    // Insert a new section with `content` right after section `index`.
    _insertSection(index, content) {
      const i = index;
      this.combinedSections.splice(i + 1, 0, content);
      if (this.batches.length) {
        this.batches.forEach((b) => {
          if (b.indexes) b.indexes = b.indexes.map((x) => (x > i ? x + 1 : x));
        });
        const kb = this.batches.find((b) => b.indexes && b.indexes.includes(i));
        const kbPos = kb ? this.batches.indexOf(kb) : this.batches.length - 1;
        this.batches.splice(kbPos + 1, 0, {
          id: "cb_new_" + (i + 1) + "_" + this.batches.length,
          indexes: [i + 1],
          status: "pending",
          markdown: "",
          error: "",
          live: "",
          serverPhase: "",
        });
      }
    },
    // Delete a Combined section entirely (there must always be at least one).
    deleteSection(index) {
      if (this.building) return;
      if (this.combinedSections.length <= 1) {
        this.showError(this.$t("kgme_deleteLastChunk"));
        return;
      }
      const i = index;
      this.combinedSections.splice(i, 1);
      if (this.batches.length) {
        // Combined batches are always single-section [i], so section i's batch becomes empty and is
        // dropped; every later section (and its batch index) shifts down by one.
        this.batches.forEach((b) => {
          if (b.indexes) b.indexes = b.indexes.filter((x) => x !== i).map((x) => (x > i ? x - 1 : x));
        });
        this.batches = this.batches.filter((b) => !b.indexes || b.indexes.length);
      }
    },
    // Commit the split using whatever the user left in the two panels. The first half stays at this
    // index; the second half becomes a new chunk inserted right after, and every later chunk shifts up
    // by one. Frontend-only: the AI path sends chunk CONTENT (not indexes) to the backend, so re-indexing
    // here needs no server round-trip.
    confirmSplit() {
      if (this.building || this.splitIndex === null) return;
      const first = (this.splitPart1 || "").trim();
      const second = (this.splitPart2 || "").trim();
      if (!first || !second) {
        this.showError(this.$t("kgme_splitBothRequired"));
        return;
      }

      // Combined section split: replace section i with [first, second]; every later section shifts up.
      if (this.splitIsSection) {
        const i = this.splitIndex;
        this.combinedSections.splice(i, 1, first, second);
        if (this.batches.length) {
          this.batches.forEach((b) => {
            if (b.indexes) b.indexes = b.indexes.map((x) => (x > i ? x + 1 : x));
          });
          const kb = this.batches.find((b) => b.indexes && b.indexes.includes(i));
          if (kb) {
            kb.status = "pending";
            kb.markdown = "";
            kb.error = "";
            kb.live = "";
            kb.serverPhase = "";
          }
          const kbPos = kb ? this.batches.indexOf(kb) : this.batches.length - 1;
          this.batches.splice(kbPos + 1, 0, {
            id: "cb_split_" + (i + 1) + "_" + this.batches.length,
            indexes: [i + 1],
            status: "pending",
            markdown: "",
            error: "",
            live: "",
            serverPhase: "",
          });
        }
        this.splitDialog = false;
        this.splitIndex = null;
        this.splitIsSection = false;
        return;
      }

      const c = this.allChunks.find((x) => x.index === this.splitIndex);
      if (!c) {
        this.splitDialog = false;
        return;
      }

      const k = c.index;
      const newIndex = k + 1;
      // Shift every chunk after k up by one (mutate the reactive proxies directly so computeds recompute).
      this.allChunks.forEach((x) => {
        if (x.index > k) x.index += 1;
      });
      // First half stays at k.
      c.content = first;
      c.charCount = first.length;
      c.preview = this.makePreview(first);
      // Insert the second half as a brand-new chunk right after k. Mark it as split-derived so its
      // delete button removes it entirely (vs. an original chunk, which only un-selects).
      const newChunk = {
        ...c,
        index: newIndex,
        content: second,
        charCount: second.length,
        preview: this.makePreview(second),
        fromSplit: true,
      };
      // Insert right after the split chunk: the first array slot whose index is beyond the new one.
      let pos = this.allChunks.findIndex((x) => x.index > newIndex);
      if (pos === -1) pos = this.allChunks.length;
      this.allChunks.splice(pos, 0, newChunk);

      // Selection: shift selected indexes after k, and select the new chunk too.
      this.selectedChunkIndexes = this.selectedChunkIndexes
        .map((i) => (i > k ? i + 1 : i))
        .concat([newIndex]);
      this.selectedChunkIndexes.sort((a, b) => a - b);

      // Build state (if any): re-index batch members after k, force the split chunk's batch to
      // regenerate, and add a fresh pending batch for the new half. Done batches are kept, so Resume
      // regenerates only the two new halves rather than the whole document.
      if (this.batches.length) {
        this.batches.forEach((b) => {
          if (b.indexes) b.indexes = b.indexes.map((i) => (i > k ? i + 1 : i));
        });
        const kb = this.batches.find((b) => b.indexes && b.indexes.includes(k));
        if (kb) {
          kb.status = "pending";
          kb.markdown = "";
          kb.error = "";
          kb.live = "";
        }
        const kbPos = kb ? this.batches.indexOf(kb) : this.batches.length - 1;
        this.batches.splice(kbPos + 1, 0, {
          id: "b_split_" + newIndex + "_" + this.batches.length,
          indexes: [newIndex],
          status: "pending",
          markdown: "",
          error: "",
          live: "",
          serverPhase: "",
        });
      }

      this.splitDialog = false;
      this.splitIndex = null;
    },
    // S16-P1 T-new: open the "add chunk" editor — a textarea to type the new chunk's content. confirmAdd
    // inserts it after `afterIndex` once the user confirms.
    addChunk(afterIndex) {
      if (this.building) return;
      this.addAfterIndex = afterIndex;
      this.addIsSection = false;
      this.addText = "";
      this.addDialog = true;
    },
    // Commit the add dialog: create the new chunk/section with the typed content.
    confirmAdd() {
      if (this.building || this.addAfterIndex === null) return;
      const content = (this.addText || "").trim();
      if (!content) return;
      if (this.addIsSection) {
        this._insertSection(this.addAfterIndex, content);
      } else {
        this._insertChunk(this.addAfterIndex, content);
      }
      this.addDialog = false;
      this.addAfterIndex = null;
      this.addText = "";
    },
    // Insert a new chunk with `content` right after chunk `afterIndex`. Mirrors the split insert (shift
    // later chunks up, add a pending batch). Marked fromSplit so its delete button removes it entirely
    // (it's user-created, not an original chunk).
    _insertChunk(afterIndex, content) {
      const c = this.allChunks.find((x) => x.index === afterIndex);
      if (!c) return;
      const k = c.index;
      const newIndex = k + 1;
      this.allChunks.forEach((x) => {
        if (x.index > k) x.index += 1;
      });
      const newChunk = {
        ...c,
        index: newIndex,
        content,
        charCount: content.length,
        preview: this.makePreview(content),
        fromSplit: true,
      };
      let pos = this.allChunks.findIndex((x) => x.index > newIndex);
      if (pos === -1) pos = this.allChunks.length;
      this.allChunks.splice(pos, 0, newChunk);

      // Selection: shift selected indexes after k, and select the new chunk too.
      this.selectedChunkIndexes = this.selectedChunkIndexes.map((i) => (i > k ? i + 1 : i)).concat([newIndex]);
      this.selectedChunkIndexes.sort((a, b) => a - b);

      // Build state (if any): re-index members after k and add a fresh pending batch for the new chunk.
      if (this.batches.length) {
        this.batches.forEach((b) => {
          if (b.indexes) b.indexes = b.indexes.map((i) => (i > k ? i + 1 : i));
        });
        const kb = this.batches.find((b) => b.indexes && b.indexes.includes(k));
        const kbPos = kb ? this.batches.indexOf(kb) : this.batches.length - 1;
        this.batches.splice(kbPos + 1, 0, {
          id: "b_new_" + newIndex + "_" + this.batches.length,
          indexes: [newIndex],
          status: "pending",
          markdown: "",
          error: "",
          live: "",
          serverPhase: "",
        });
      }
    },
    // Delete button. Behaviour depends on where the chunk came from:
    //  • split-derived chunk (fromSplit) → remove it ENTIRELY and shift every later chunk back down by
    //    one, so the chunk that followed the split reverts to the index it had before the split.
    //  • original chunk → just UN-SELECT it (stays in allChunks with its index, can be re-picked on the
    //    Select-chunks step). Nothing is re-indexed.
    deleteChunk(index) {
      if (this.building) return;
      const k = index;
      const c = this.allChunks.find((x) => x.index === k);
      if (!c) return;
      // Never remove the last chunk left to convert — there must be something to build.
      if (this.selectedChunkIndexes.filter((i) => i !== k).length === 0) {
        this.showError(this.$t("kgme_deleteLastChunk"));
        return;
      }

      if (c.fromSplit) {
        // Hard delete + re-index: drop it and pull every later chunk down by one.
        const pos = this.allChunks.indexOf(c);
        this.allChunks.splice(pos, 1);
        this.allChunks.forEach((x) => {
          if (x.index > k) x.index -= 1;
        });
        this.selectedChunkIndexes = this.selectedChunkIndexes.filter((i) => i !== k).map((i) => (i > k ? i - 1 : i));
        if (this.batches.length) {
          const kb = this.batches.find((b) => b.indexes && b.indexes.includes(k));
          this.batches.forEach((b) => {
            if (b.indexes) b.indexes = b.indexes.filter((i) => i !== k).map((i) => (i > k ? i - 1 : i));
          });
          this.batches = this.batches.filter((b) => !b.indexes || b.indexes.length);
          if (kb && kb.indexes && kb.indexes.length) {
            kb.status = "pending";
            kb.markdown = "";
            kb.error = "";
            kb.live = "";
            kb.serverPhase = "";
          }
        }
        return;
      }

      // Original chunk → un-select only (indexes of the other chunks are unchanged).
      if (!this.selectedChunkIndexes.includes(k)) return;
      this.selectedChunkIndexes = this.selectedChunkIndexes.filter((i) => i !== k);
      if (this.batches.length) {
        const kb = this.batches.find((b) => b.indexes && b.indexes.includes(k));
        this.batches.forEach((b) => {
          if (b.indexes) b.indexes = b.indexes.filter((i) => i !== k);
        });
        this.batches = this.batches.filter((b) => !b.indexes || b.indexes.length);
        if (kb && kb.indexes && kb.indexes.length) {
          kb.status = "pending";
          kb.markdown = "";
          kb.error = "";
          kb.live = "";
          kb.serverPhase = "";
        }
      }
    },
    makePreview(text) {
      const t = (text || "").replace(/\s+/g, " ").trim();
      return t.length > 120 ? t.slice(0, 120) + "…" : t;
    },
    // S16-P1 T9-3: convert-done dialog actions.
    gotoCreatedGraph() {
      this.convertDoneDialog = false;
      if (this.createdGraphId) {
        this.$router.push({ name: "KnowledgeGraphDetail", params: { graphKey: this.createdGraphId } }); // S16-P1 (D4): bal route /KnowledgeGraphs/:graphKey
      } else {
        this.$router.push({ name: "VectorManagement" });
      }
    },
    gotoDocumentList() {
      this.convertDoneDialog = false;
      this.$router.push({ name: "VectorManagement" });
    },
    // Top-left arrow / per-step "Back": step back one screen. Nothing is persisted (S16-P1: GraphRAG engine) before
    // Confirm, so stepping back needs no cleanup. From the first step, leave to the Document List.
    onBack() {
      switch (this.step) {
        case "preview":
          this.step = "map";
          break;
        case "map":
          this.step = "edit";
          break;
        case "edit":
          this.step = this.useLlm ? "llm" : "mode";
          break;
        case "llm":
          this.step = "mode";
          break;
        case "mode":
          this.step = "chunks";
          break;
        default:
          this.$router.push({ name: "VectorManagement" });
      }
    },
  },
};
</script>

<style scoped>
.kg-md-editor-page {
  max-width: 1000px;
  margin: 0 auto;
  padding-bottom: 24px;
}
.card2 {
  border-radius: 16px !important;
  position: relative;
  overflow: hidden;
}
.kg-stepper-wrap {
  overflow-x: auto;
}
.kg-stepper {
  box-shadow: none !important;
  background: transparent;
}
.kg-stepper :deep(.v-stepper-header) {
  flex-wrap: nowrap;
  box-shadow: none;
  min-width: max-content;
}
/* S16-P1 T9-1: a visited step the user can click to jump to. */
.kg-stepper :deep(.v-stepper-item.kg-step-clickable) {
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.15s ease;
}
.kg-stepper :deep(.v-stepper-item.kg-step-clickable:hover) {
  background-color: rgba(195, 46, 14, 0.06);
}
.kg-step-body {
  min-height: 200px;
}
.kg-choice-card {
  border-radius: 12px !important;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.kg-choice-card--active {
  border-color: #c32e0e !important;
  background-color: rgba(195, 46, 14, 0.06);
}
.kg-chunk-list,
.kg-batch-list {
  max-height: 420px;
  overflow-y: auto;
}
.kg-chunk-preview {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kg-batch-error {
  border-color: #d33 !important;
}
.kg-batch-error-msg {
  color: #d33;
  white-space: pre-wrap;
  word-break: break-word;
}
.kg-md-textarea :deep(textarea) {
  font-family: "Consolas", "Courier New", monospace;
  font-size: 13px;
}
/* Split editor: fill the fullscreen dialog so both halves are big, full-page panels. */
.kg-split-textarea :deep(textarea) {
  min-height: calc(100vh - 260px);
}
/* Gentle pulse for the live LLM-phase icon (reading / generating) under the progress bar. */
.kg-pulse {
  animation: kg-pulse 1.2s ease-in-out infinite;
}
@keyframes kg-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}
/* Configure step: keep the prompt box a fixed height with its own scroll (halved column width). */
.kg-config-prompt :deep(textarea) {
  max-height: 220px;
  overflow-y: auto;
}
/* Combined mode: the single combined-document box scrolls internally instead of growing the column. */
.kg-combined-textarea :deep(textarea) {
  max-height: 60vh;
  overflow-y: auto;
}
/* Edit Markdown: bordered, fixed-height box that scrolls internally instead of stretching the page. */
.kg-edit-textarea :deep(textarea) {
  max-height: 72vh;
  overflow-y: auto;
}
/* Expand popup textarea: tall, scrolls internally. */
.kg-expand-textarea :deep(textarea) {
  max-height: 65vh;
  overflow-y: auto;
}
/* Realtime live-generation preview: monospace, fixed height, scrolls to the newest tokens. */
.kg-live-box {
  margin: 0;
  padding: 10px 12px;
  max-height: 260px;
  overflow-y: auto;
  background: #1e1e1e;
  color: #e6e6e6;
  border-radius: 8px;
  border: 1px solid rgba(195, 46, 14, 0.35);
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
.kg-live-caret {
  color: #C32E0E;
  animation: kg-live-blink 1s step-start infinite;
}
@keyframes kg-live-blink {
  50% {
    opacity: 0;
  }
}
.kg-preview-tree {
  list-style: none;
  padding-left: 0;
  margin: 0;
}
.kg-preview-tree li {
  padding: 3px 0;
}
.kg-node-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
  flex: 0 0 auto;
  vertical-align: middle;
}
.kg-node-dot--skip {
  border: 1px dashed #9e9e9e;
  background-color: transparent !important;
}
</style>
